import { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { boxWeightWarning, carrierMaxParcelGrams, type WeightWarning } from "./packWeightWarnings";
import { ArrowLeft, Check, X, AlertTriangle, PackageMinus, Plus, Package, PackageOpen, Trash2, ScanLine, ScanBarcode, Flag, RefreshCw, Lightbulb, Ruler, Weight, PackageX, Shrink, Shield, ShieldAlert, MoveDiagonal, CircleDollarSign, RotateCcw, Undo2, ChevronsDown, Search } from "lucide-react";
import { playSuccess, playError, playComplete, playBoxAdd } from "../helpers/audioPlayer";
import { useTranslation } from "../../i18n";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { logoutProcessor } from "../helpers/logoutProcessor";
import HiddenScanInput from "../HiddenScanInput";
import type { DeskInfo, DispatchAddress, DispatchOrder, DispatchOrderItem, DispatchBox, CourierBookingResult, CartonComponent, OwnPackagingSpec } from "../../types/Dispatch";
import { useDemoVisible } from "../helpers/demoMode";
import DispatchBookingStatus from "./dispatch-booking-status";
import type { CarrierPanelState } from "./dispatch-booking-status";
import { recommendBoxes, fitsInSingleBox, ShippingTier } from "./boxRecommender";
import type { RecommenderBox, RecommenderComponent } from "./boxRecommender";
import { scannableBarcodes, displayBarcodes } from "./itemBarcodes";
import { recoverScanCaseReported } from "../helpers/scanCase";
import { classifyUnknownScan, unknownScanMessageKey, shouldIdentify, describeIdentifiedScan } from "./unknownScan";
import type { IdentifiedBarcode } from "./unknownScan";
import { logDispatchEvent } from "./dispatchLog";
import { apiFetch } from "../../api/apiFetch";
import { useReadOnly } from "../../access/ReadOnly";
import { hasPendingFlaggedPick, stagingShelfLabel } from "./flagged-pick-notice";
import { resetOrderState, hasDeskProgress } from "./deskOrderState";
import DispatchOrderBar from "./dispatch-order-bar";
import { DispatchSessionStats } from "./dispatch-session-chrome";
import { RESOLVED_HOLD_MS, countRowsBelow, sortForPacking } from "./packList";
import { findSearchHit, hitsFirst, type SearchHit } from "./packSearch";
import { isBoxAssigned, isOwnPackagingBox, kgField, measuredContentsGrams, ownPackagingDefaults, ownPackagingUnits as ownPackagingUnitsFor, planOwnPackaging } from "./ownPackaging";
import type { OwnPackagingDefaults } from "./ownPackaging";
import "../../styles/flagged-picks.css";
import "../../styles/dispatch-bench.css";

// DEMO_MODE is sourced per-render from useDemoVisible() inside the component.

// Override reasons offered when the operator scans a different box to the
// recommended one. Codes are stored verbatim in dispatch_box_recommendations
// so the analytics stay language-independent; labels come from i18n
// (box_override_reason_<code>). Deliberately no "Other" and no dismiss —
// a concrete reason is required, so the override data can't be diluted
// by reflexive skipping.
const OVERRIDE_REASONS = [
    { code: "items_dont_fit", icon: Ruler },
    { code: "items_too_heavy", icon: Weight },
    { code: "box_unavailable", icon: PackageX },
    { code: "smaller_box_fits", icon: Shrink },
    { code: "extra_protection", icon: Shield },
] as const;

const getCurrentUser = (): { uid: string; name: string } => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return { uid: "", name: "" };
        const u = JSON.parse(raw);
        return { uid: u?.uid ?? "", name: u?.name ?? "" };
    } catch { return { uid: "", name: "" }; }
};

// isBoxAssigned (ownPackaging.ts): a real box was scanned OR measured. It
// drives both the Complete gate (hasScannedBox) and the red "needs a scan"
// tab styling — keep the two in sync by always going through it.
// Carton contents in the recommender's shape. Only meaningful for a line
// whose product has no dims of its own (the recommender ignores them
// otherwise); undefined when the BE supplied none.
const recommenderComponents = (i: DispatchOrderItem): RecommenderComponent[] | undefined =>
    i.components?.map(c => ({
        asin: c.asin,
        units: c.units,
        lengthCm: c.length_cm,
        widthCm: c.width_cm,
        heightCm: c.height_cm,
    }));

// The search match, with the matching characters marked.
const SearchMarked = ({ hit }: { hit: SearchHit }) => (
    <>
        {hit.value.slice(0, hit.start)}
        <mark className="dispatch-search-mark">{hit.value.slice(hit.start, hit.end)}</mark>
        {hit.value.slice(hit.end)}
    </>
);

// Up to two scannable barcodes on the SKU line, "+N" when more exist.
// Hidden when the item's only barcode is the SKU fallback.
const ItemBarcodes = ({ item }: { item: DispatchOrderItem }) => {
    const { shown, hidden } = displayBarcodes(item);
    if (shown.length === 0) return null;
    return (
        <span className="dispatch-item-barcodes">
            {shown.join(" · ")}
            {hidden > 0 && <span className="dispatch-item-barcodes-more">+{hidden}</span>}
        </span>
    );
};

type Props = {
    order: DispatchOrder;
    boxes: DispatchBox[];
    deskInfo: DeskInfo;
    suppressAutoModal?: boolean;
    onUpdateOrder: (order: DispatchOrder) => void;
    onUpdateBoxes: (boxes: DispatchBox[]) => void;
    onComplete: (result: CourierBookingResult, boxes: DispatchBox[]) => void;
    onItemScanned?: (count?: number) => void;
    onFlagOrder?: () => void;
    goBack: () => void;
    // A carrier label that is already booked for this order - set by the
    // page when the packer came BACK from Confirm Shipment (the bench
    // auto-books the moment the last unit is scanned). `pieces` is how many
    // parcels the label covers; when the bench holds a different number the
    // box bar warns, and Complete will void the label and book again. Also
    // learned from a booking_box_mismatch refusal. Not restored after a
    // reload - the BE guard still catches the mismatch then.
    liveBooking?: { id: number; pieces: number; carrierName?: string | null } | null;
    // A two-cubby Putwall order went out in two totes — list them under the
    // title so the packer fetches the second before packing. Only passed
    // when there is more than one.
    unitTotes?: { tote_id: string; items: number }[];
    // Flagged-order resolution (dispatch-flagged-resolve.tsx) reuses this
    // screen so leaders get the identical pack experience — recommendation,
    // box scanning, telemetry, everything. Differences in resolve mode:
    //   • the flag-action dropdown becomes "Refund" (stored as missingCount
    //     here; the resolve page converts it to refundCount on persist) +
    //     the barcode-damaged manual-ship action;
    //   • when EVERY unit is refunded there's no shipment to book — the
    //     Complete button calls onFinishNoShipment instead;
    //   • flagInfo renders the flag-reason banner under the title.
    // Desk session (all optional — the flagged-resolve screen passes none):
    //   initialActiveBox  the box tab to start on (restored from the server)
    //   onActiveBoxChange the page keeps it so it is saved with the pack state
    //   onBeforeBook      awaited before a carrier booking, so the saved
    //                     desk is never behind the booking
    //   onOrderReset      called as Reset order clears the desk, so the page
    //                     saves the empty state at once
    initialActiveBox?: number;
    onActiveBoxChange?: (boxId: number) => void;
    onBeforeBook?: () => Promise<void>;
    onOrderReset?: () => void;
    resolveMode?: {
        onFinishNoShipment: () => void;
        flagInfo?: { reason: string; by?: string } | null;
        /** E9: false when this leader holds dispatch.flagged.dispatch but not
         *  dispatch.flagged.refund — the per-item Refund option is disabled so
         *  they cannot book a shipment and then be refused at resolve time. */
        canRefund?: boolean;
    };
};

const DispatchOrderPack = ({ order, boxes, deskInfo, suppressAutoModal, onUpdateOrder, onUpdateBoxes, onComplete, onItemScanned, onFlagOrder, goBack, liveBooking, unitTotes, resolveMode, initialActiveBox, onActiveBoxChange, onBeforeBook, onOrderReset }: Props) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const DEMO_MODE = useDemoVisible();
    const [scan, setScan] = useState("");
    const [activeBox, setActiveBox] = useState(() =>
        initialActiveBox !== undefined && boxes.some(b => b.id === initialActiveBox) ? initialActiveBox : 1);
    useEffect(() => { onActiveBoxChange?.(activeBox); }, [activeBox]); // eslint-disable-line react-hooks/exhaustive-deps
    const [confirmReset, setConfirmReset] = useState(false);
    const [booking, setBooking] = useState(false);
    const [flashItemId, setFlashItemId] = useState<string | null>(null);
    const [newBoxId, setNewBoxId] = useState<number | null>(null);
    const [shakeItemId, setShakeItemId] = useState<string | null>(null);
    const [dropdownItemId, setDropdownItemId] = useState<string | null>(null);
    const [confirmRemoveBoxId, setConfirmRemoveBoxId] = useState<number | null>(null);
    // Leaving an order that already has progress asks first (the back
    // arrow in the title bar) — scans/boxes are discarded on exit.
    const [confirmLeave, setConfirmLeave] = useState(false);
    // Box scans always go through validate-and-assign. When set to a tab id,
    // the next valid box scan reassigns THAT tab; otherwise the scan either
    // assigns an unassigned active box or creates a new box.
    const [reassignTargetId, setReassignTargetId] = useState<number | null>(null);
    // "+ tab" modal — operator can scan a real box (closes itself once
    // the global scan handler has assigned/created the tab) OR enter
    // one-off custom dimensions for an ad-hoc box.
    const [showAddBoxModal, setShowAddBoxModal] = useState(false);
    // Dimensions only. The weight field used to sit here too, but it was
    // the PACKAGING TARE (dispatch-close-order adds it to the summed item
    // weights) — a number the operator has no way to know at the bench for
    // an ad-hoc box, and guessing it mis-declares the parcel. Custom boxes
    // now book with tare 0, the same as own-packaging boxes always have.
    const [customBoxForm, setCustomBoxForm] = useState({ length: "", width: "", height: "" });
    // "Ship in own packaging" prompt. Opened by the item's own-packaging
    // button, which used to be DISABLED whenever the product had no
    // dimensions — a dead end at the bench, since the operator is holding
    // the item and can measure it. The modal captures the container's
    // dimensions (pre-filled from the product when known) and HOW MANY
    // units go into this one container, so several of the same SKU strapped
    // together book as a single parcel instead of one parcel per unit.
    // `prefer`: which units go in first (ownPackaging.ts planOwnPackaging) —
    // "boxed" from the line's button, "fresh" when the item was scanned
    // after "+ › Own packaging" (the unit in hand is an unscanned one).
    // `defaults`: what the dialog opened with (ownPackaging.ts
    // ownPackagingDefaults) — the saved measurement, the catalogue size or
    // nothing — so the hint can say where the numbers came from and the
    // weight can follow the Units field until the packer types one.
    const [ownPkgPrompt, setOwnPkgPrompt] = useState<{ itemId: string; prefer: "boxed" | "fresh"; defaults: OwnPackagingDefaults } | null>(null);
    // `weight` is kg, as read off the scale, for the whole container.
    // `weightEdited`: the packer typed it, so Units no longer rescales it.
    const [ownPkgForm, setOwnPkgForm] = useState({ length: "", width: "", height: "", qty: "1", weight: "", weightEdited: false });
    // The last confirmed own-packaging measurement per SKU on this order
    // (GET /dispatch/own-packaging, migration 247), and each SKU's ASIN so a
    // fresh measurement can prefill other lines of the same product.
    const [ownPkgMemory, setOwnPkgMemory] = useState<{ specs: Record<string, OwnPackagingSpec>; asins: Record<string, string> }>({ specs: {}, asins: {} });
    // "+" in the box bar opens a small menu above it: New box · Own
    // packaging · Other size. Fixed-positioned from the + button, because
    // the box tab row clips anything that overflows it.
    const [boxMenu, setBoxMenu] = useState<{ left: number; bottom: number } | null>(null);
    // "+ › Own packaging" picked: a blue SCAN ITEM tab waits, and the next
    // item scan goes into its own packaging. Screen state only — the real
    // box is made when the item arrives, so nothing half-made is ever saved.
    const [ownWait, setOwnWait] = useState(false);
    // Barcode search (2026-10-26). While open, EVERY scan goes into the
    // search box and only searches, until Close.
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    // One-shot shake on the box tab still waiting for its box scan, when
    // Complete is clicked before any box has been scanned.
    const [promptShake, setPromptShake] = useState(false);
    // The scrolling item list, and how many lines sit (wholly or partly)
    // below its visible edge — drives the "More below: N" pill.
    const listRef = useRef<HTMLDivElement>(null);
    const [moreBelow, setMoreBelow] = useState(0);
    // Which way the issue menu opens: up when its row is low in the list,
    // so the list's own scroll edge never clips it.
    const [dropdownUp, setDropdownUp] = useState(false);
    const prevBoxCount = useRef(0);
    // Full warehouse box list — used by the box recommender (and also
    // sliced for the demo button row). Empty until the fetch settles or
    // when no warehouse is selected.
    const [warehouseBoxes, setWarehouseBoxes] = useState<RecommenderBox[]>([]);
    // True once the boxes fetch has settled (success OR failure). The
    // telemetry outcome check defers until then — a box scanned in the
    // first seconds of an order must not silently skip the override
    // modal just because the catalogue hadn't arrived yet.
    const [boxesLoaded, setBoxesLoaded] = useState(false);
    useEffect(() => {
        if (!deskInfo.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/dispatch-admin/boxes?warehouse=${encodeURIComponent(deskInfo.warehouse)}`,
                    {},
                );
                if (!alive) return;
                if (!res.ok) { setBoxesLoaded(true); return; }
                type ApiBox = { barcode: string; name: string;
                                length_mm?: number; width_mm?: number; height_mm?: number;
                                weight_grams?: number; max_weight_grams?: number | null; max_fill_mm?: number | null; current_stock?: number;
                                wall_type?: "single" | "double" | "mailbag" | "cardboard_mailbag";
                                is_primary_large?: boolean;
                                unit_cost?: number | string | null };
                const data: { boxes?: ApiBox[] } = await res.json();
                if (!alive) return;
                const list: RecommenderBox[] = (data.boxes ?? []).map(b => ({
                    barcode: b.barcode,
                    name: b.name,
                    lengthMm: Number(b.length_mm) || 0,
                    widthMm: Number(b.width_mm) || 0,
                    heightMm: Number(b.height_mm) || 0,
                    weightGrams: Number(b.weight_grams) || 0,
                    maxWeightGrams: b.max_weight_grams == null ? null : (Number(b.max_weight_grams) || null),
                    // Plastic mailbags: max filled thickness (never height_mm).
                    maxFillMm: b.max_fill_mm == null ? null : (Number(b.max_fill_mm) || null),
                    currentStock: Number(b.current_stock) || 0,
                    wallType: b.wall_type === "double" || b.wall_type === "mailbag" || b.wall_type === "cardboard_mailbag" ? b.wall_type : "single",
                    isPrimaryLarge: b.is_primary_large === true,
                    // Second ranking key for the recommender — null when
                    // the box has no cost recorded in Settings.
                    unitCost: b.unit_cost === null || b.unit_cost === undefined || b.unit_cost === ""
                        ? null
                        : Number(b.unit_cost),
                }));
                setWarehouseBoxes(list);
                setBoxesLoaded(true);
            } catch {
                /* leave empty — recommender just hides the banner */
                if (alive) setBoxesLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [deskInfo.warehouse]);

    // The warehouse's shipping options, so the recommender can rank
    // containers by what they cost to POST as well as to buy — the box
    // name is what gates the MP16/CP16 pack rates, so the container
    // choice sets the postage. Failure leaves this empty, which simply
    // returns the recommender to smallest-first.
    const [shippingTiers, setShippingTiers] = useState<ShippingTier[]>([]);
    useEffect(() => {
        if (!deskInfo.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/carriers/shipping-tiers?warehouse=${encodeURIComponent(deskInfo.warehouse)}`,
                    {},
                );
                if (!alive || !res.ok) return;
                const data: { tiers?: ShippingTier[] } = await res.json();
                if (alive && Array.isArray(data.tiers)) setShippingTiers(data.tiers);
            } catch { /* no tiers → smallest-first, as before */ }
        })();
        return () => { alive = false; };
    }, [deskInfo.warehouse]);

    // Demo button row uses a small subset of the same list.
    const demoBoxes = useMemo(
        () => DEMO_MODE ? warehouseBoxes.slice(0, 6).map(b => ({ barcode: b.barcode, name: b.name })) : [],
        [DEMO_MODE, warehouseBoxes],
    );

    // Per-warehouse dispatch settings (show_images, box_recommendation,
    // tolerances). Cached in sessionStorage keyed by warehouse so reopening
    // dispatch reads the last-known values synchronously — no flash of
    // images for warehouses that have them off, and no flash of the
    // recommendation banner for warehouses that have it disabled. The
    // fetch below refreshes the cache in the background.
    const cacheKey = `dispatchSettings:${deskInfo.warehouse || "unknown"}`;
    const cached = (() => {
        try {
            const raw = window.sessionStorage.getItem(cacheKey);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    })();
    const [showImages, setShowImages] = useState<boolean>(cached?.showImages ?? true);
    const [boxRecommendationEnabled, setBoxRecommendationEnabled] = useState<boolean>(cached?.boxRecommendationEnabled ?? true);
    const [voidFillPct, setVoidFillPct] = useState<number>(cached?.voidFillPct ?? 15);
    const [packingToleranceMm, setPackingToleranceMm] = useState<number>(cached?.packingToleranceMm ?? 10);
    const [stockAwareRecommendation, setStockAwareRecommendation] = useState<boolean>(cached?.stockAwareRecommendation ?? false);
    // Diagonal placement (ON by default): long items may be recommended a
    // smaller container they fit rotated-in-plane, with their own (larger)
    // tolerances — see boxRecommender.ts "Diagonal placement".
    const [diagonalEnabled, setDiagonalEnabled] = useState<boolean>(cached?.diagonalEnabled ?? true);
    const [diagonalToleranceMm, setDiagonalToleranceMm] = useState<number>(cached?.diagonalToleranceMm ?? 10);
    const [diagonalBagToleranceMm, setDiagonalBagToleranceMm] = useState<number>(cached?.diagonalBagToleranceMm ?? 10);
    // Box names an extra-protection ("fragile") item may not travel in.
    // Empty = nothing excluded; the warehouse's plastic mailbags were
    // seeded into this list by migration 131, replacing the old hardcoded
    // "no plastic mailbag" rule.
    const [extraProtectionExcludedBoxes, setExtraProtectionExcludedBoxes] = useState<string[]>(
        Array.isArray(cached?.extraProtectionExcludedBoxes) ? cached.extraProtectionExcludedBoxes : [],
    );
    // Same deferral flag as boxesLoaded — settled once the settings
    // fetch completes (or fails, keeping the cached/default values).
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    useEffect(() => {
        if (!deskInfo.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(deskInfo.warehouse)}`,
                    {},
                );
                if (!alive) return;
                if (!res.ok) { setSettingsLoaded(true); return; }
                const data = await res.json();
                if (!alive) return;
                const next = {
                    showImages: typeof data.dispatch_show_images === "boolean" ? data.dispatch_show_images : true,
                    boxRecommendationEnabled: typeof data.dispatch_box_recommendation_enabled === "boolean" ? data.dispatch_box_recommendation_enabled : true,
                    stockAwareRecommendation: data.dispatch_stock_aware_recommendation === true,
                    diagonalEnabled: typeof data.dispatch_diagonal_recommendation === "boolean" ? data.dispatch_diagonal_recommendation : true,
                    // JSONB array of dispatch-box names; anything else
                    // (legacy row, malformed) means "exclude nothing".
                    extraProtectionExcludedBoxes: Array.isArray(data.dispatch_extra_protection_excluded_boxes)
                        ? data.dispatch_extra_protection_excluded_boxes.filter((n: unknown): n is string => typeof n === "string")
                        : [],
                    voidFillPct: 15,
                    packingToleranceMm: 10,
                    diagonalToleranceMm: 10,
                    diagonalBagToleranceMm: 10,
                };
                // NUMERIC comes back as a string ("15.00"), INTEGER as a number.
                const vfp = data.dispatch_void_fill_pct;
                if (vfp !== null && vfp !== undefined && vfp !== "") {
                    const n = Number(vfp);
                    if (Number.isFinite(n) && n >= 0) next.voidFillPct = n;
                }
                const ptm = data.dispatch_packing_tolerance_mm;
                if (ptm !== null && ptm !== undefined && ptm !== "") {
                    const n = Number(ptm);
                    if (Number.isFinite(n) && n >= 0) next.packingToleranceMm = n;
                }
                const dtm = data.dispatch_diagonal_tolerance_mm;
                if (dtm !== null && dtm !== undefined && dtm !== "") {
                    const n = Number(dtm);
                    if (Number.isFinite(n) && n >= 0) next.diagonalToleranceMm = n;
                }
                const dbtm = data.dispatch_diagonal_bag_tolerance_mm;
                if (dbtm !== null && dbtm !== undefined && dbtm !== "") {
                    const n = Number(dbtm);
                    if (Number.isFinite(n) && n >= 0) next.diagonalBagToleranceMm = n;
                }
                setShowImages(next.showImages);
                setBoxRecommendationEnabled(next.boxRecommendationEnabled);
                setStockAwareRecommendation(next.stockAwareRecommendation);
                setDiagonalEnabled(next.diagonalEnabled);
                setExtraProtectionExcludedBoxes(next.extraProtectionExcludedBoxes);
                setDiagonalToleranceMm(next.diagonalToleranceMm);
                setDiagonalBagToleranceMm(next.diagonalBagToleranceMm);
                setVoidFillPct(next.voidFillPct);
                setPackingToleranceMm(next.packingToleranceMm);
                try { window.sessionStorage.setItem(cacheKey, JSON.stringify(next)); } catch { /* quota / private mode */ }
                setSettingsLoaded(true);
            } catch {
                /* leave defaults on failure */
                if (alive) setSettingsLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [deskInfo.warehouse]);

    // Hydrate dimensions onto order items — the dispatch /orders feed only
    // returns weight, but the recommender needs L/W/H from /products. Done
    // once per order_id; SKUs missing from the catalogue stay undimensioned
    // and are skipped by the recommender (with a warning chip).
    const hydratedOrderIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (hydratedOrderIdRef.current === order.order_id) return;
        const skus = Array.from(new Set(order.items.map(i => i.sku).filter(Boolean)));
        if (skus.length === 0) return;
        // Skip the network call if every item already has dims (e.g. demo
        // mode pre-hydrated by loadDemoProductsOnce).
        const allHaveDims = order.items.every(i =>
            i.length_cm != null && i.width_cm != null && i.height_cm != null,
        );
        if (allHaveDims) {
            hydratedOrderIdRef.current = order.order_id;
            return;
        }
        let alive = true;
        (async () => {
            try {
                // include=components: a Jobalots carton with no dims of
                // its own comes back either WITH dims (a single-item
                // carton — the item's package dims, which the BE also
                // writes to the product) or with `components`, the ASINs
                // inside it for the recommender to pack instead.
                const res = await apiFetch(
                    `/products/by-skus?skus=${encodeURIComponent(skus.join(","))}&include=components`,
                    {},
                );
                if (!res.ok || !alive) return;
                type ProdRow = { sku: string;
                                 title: string | null;
                                 image_url: string | null;
                                 barcode: string | null;
                                 barcodes?: string[];
                                 length_cm: number | string | null;
                                 width_cm: number | string | null;
                                 height_cm: number | string | null;
                                 weight_grams: number | null;
                                 ship_in_packaging: boolean;
                                 soft_item?: boolean;
                                 components?: CartonComponent[] };
                const data: { products?: ProdRow[] } = await res.json();
                const bySku = new Map<string, ProdRow>();
                for (const p of (data.products ?? [])) bySku.set(p.sku, p);
                const merged: DispatchOrderItem[] = order.items.map(i => {
                    const p = bySku.get(i.sku);
                    if (!p) return i;
                    return {
                        ...i,
                        title:     i.title     || p.title || i.sku,
                        image_url: i.image_url || p.image_url,
                        barcode:   i.barcode   || p.barcode || i.sku,
                        barcodes:  (i.barcodes?.length ? i.barcodes : p.barcodes) ?? i.barcodes,
                        length_cm: i.length_cm ?? p.length_cm,
                        width_cm:  i.width_cm  ?? p.width_cm,
                        height_cm: i.height_cm ?? p.height_cm,
                        components: i.components ?? p.components,
                        weight_grams: i.weight_grams || (p.weight_grams ?? 0),
                        ship_in_packaging: i.ship_in_packaging ?? p.ship_in_packaging,
                        // NOT hydrated from the product row: the orders
                        // feed is the authority here because it applies
                        // the channel's AI extra-protection toggle. A
                        // channel with the toggle OFF sends false for
                        // every item, and the catalogue value must not
                        // put the flag back.
                        extra_protection: i.extra_protection === true,
                        // A catalogue fact, not channel policy: either source counts.
                        soft_item: i.soft_item === true || p.soft_item === true,
                    };
                });
                if (!alive) return;
                hydratedOrderIdRef.current = order.order_id;
                onUpdateOrder({ ...order, items: merged });
            } catch { /* leave dims undefined; recommender skips */ }
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order.order_id]);

    // What the last packer measured for this order's SKUs when they shipped
    // in their own packaging — per SKU, or shared with every SKU of the same
    // ASIN (Jobalots cartons are unique SKUs of repeated products). Fetched
    // once per order and separately from the dims hydration above, which is
    // skipped when every line already has dims. Fail-soft: no answer means
    // the dialog prefills from the product.
    useEffect(() => {
        setOwnPkgMemory({ specs: {}, asins: {} });
        const skus = Array.from(new Set(order.items.map(i => i.sku).filter(Boolean)));
        if (skus.length === 0) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/dispatch/own-packaging?skus=${encodeURIComponent(skus.join(","))}`, {});
                if (!res.ok || !alive) return;
                const data: { specs?: Record<string, OwnPackagingSpec>; asins?: Record<string, string> } = await res.json();
                if (!alive) return;
                setOwnPkgMemory({ specs: data?.specs ?? {}, asins: data?.asins ?? {} });
            } catch { /* nothing remembered — prefill from the product */ }
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order.order_id]);

    // Resolution helpers — an item is fully resolved when every unit has been
    // accounted for (scanned, missing, damaged, or barcode-damaged).
    // Defaults guard against session-stored orders saved before the new
    // count fields existed, which would otherwise yield NaN.
    const resolvedCount = (item: DispatchOrderItem) =>
        (item.scannedCount || 0)
        + (item.missingCount || 0)
        + (item.damagedCount || 0)
        + (item.wrongItemCount || 0)
        + (item.barcodeDamagedCount || 0);
    const shippedCount = (item: DispatchOrderItem) =>
        (item.scannedCount || 0) + (item.barcodeDamagedCount || 0);
    const isItemResolved = (item: DispatchOrderItem) => resolvedCount(item) >= item.quantity;

    // NOTE: there is deliberately NO auto-fill for ship_in_packaging items.
    // Until 2026-09-09 a flagged product with dimensions was pre-boxed the
    // moment the order opened: one "Own packaging" box per unit, every unit
    // marked shipped, the row already green. The flag is mostly set by the
    // pack desk's own "Ship in own packaging" button, i.e. it records ONE
    // operator's call on ONE order — and the next order for that SKU
    // routinely wanted a real box (bargainfox:344213, five pre-made boxes
    // nobody asked for). The flag now only highlights the button below as
    // a hint; the operator decides every time.

    // Bench-side "this one is fragile": treat EVERY item in the order as
    // extra-protection, which drops the containers the warehouse excludes
    // (Settings > Dispatch > Box Recommendation — the plastic mailbags)
    // and re-recommends the next thing that fits. Same mechanism as the
    // product-level extra_protection flag, decided at the desk for the
    // orders nobody flagged in advance.
    const [fragileOverride, setFragileOverride] = useState(false);

    // A per-order decision.
    useEffect(() => {
        setFragileOverride(false);
    }, [order.order_id]);

    // Compute the box recommendation.
    // Until the operator has actually CHOSEN a box (scanned a real box
    // barcode or entered custom dims), every scanned item is still in
    // their hand — the auto-assign-to-Box-1 is conceptual only. So the
    // recommendation has to consider the WHOLE order, not just the
    // unscanned remainder, otherwise we'd suggest a box that's too small
    // (it would ignore the item the operator just scanned to open the
    // order). Once a box is chosen, scanned items are physically in it
    // and we pivot to "remaining only" so the recommendation can
    // downgrade to a smaller box for the rest.
    const recommendation = useMemo(() => {
        const anyBoxAssigned = boxes.some(isBoxAssigned);
        const diagonal = { enabled: diagonalEnabled, toleranceMm: diagonalToleranceMm, bagToleranceMm: diagonalBagToleranceMm };
        const itemDims = (i: DispatchOrderItem) => ({
            lengthCm: i.length_cm ?? null,
            widthCm: i.width_cm ?? null,
            heightCm: i.height_cm ?? null,
            components: recommenderComponents(i),
            extraProtection: fragileOverride || i.extra_protection === true,
            soft: i.soft_item === true,
            weightGrams: i.weight_grams,
        });
        if (!anyBoxAssigned) {
            // No box chosen yet — recommend for the whole order. Full
            // quantity minus units flagged missing/damaged/wrong (those
            // never take box space); shipped units stay counted since
            // they still need room in the eventual box.
            return recommendBoxes({
                items: order.items.map(i => ({
                    sku: i.sku,
                    quantity: Math.max(0, i.quantity - (resolvedCount(i) - shippedCount(i))),
                    ...itemDims(i),
                })),
                boxes: warehouseBoxes,
                voidFillPct,
                packingToleranceMm,
                considerStock: stockAwareRecommendation,
                diagonal,
                extraProtectionExcludedBoxes,
                shippingTiers,
            });
        }

        // A box is in use. Only suggest a "next box" when what's left
        // genuinely doesn't fit an assigned box together with its current
        // contents — otherwise the banner would tell the operator to grab
        // a second box they don't need (e.g. a letterbox M1 for the last
        // small item while the open box still has plenty of room).
        const remainingItems = order.items
            .filter(i => !isItemResolved(i))
            .map(i => ({
                sku: i.sku,
                quantity: Math.max(0, i.quantity - resolvedCount(i)),
                ...itemDims(i),
            }));
        const remainderFitsAssigned = boxes.some(b => {
            if (!isBoxAssigned(b) || !b.boxLengthMm || !b.boxWidthMm || !b.boxHeightMm) return false;
            const inThisBox = order.items
                .map(i => ({
                    sku: i.sku,
                    quantity: i.assignedBoxes.filter(id => id === b.id).length,
                    ...itemDims(i),
                }))
                .filter(x => x.quantity > 0);
            const catalogueBox = warehouseBoxes.find(wb => wb.barcode === b.boxRef);
            return fitsInSingleBox({
                items: [...inThisBox, ...remainingItems],
                box: {
                    // Undefined for a custom-dimension box entered at the
                    // bench — those are never on the excluded list.
                    name: catalogueBox?.name,
                    lengthMm: b.boxLengthMm,
                    widthMm: b.boxWidthMm,
                    heightMm: b.boxHeightMm,
                    wallType: catalogueBox?.wallType,
                    maxFillMm: catalogueBox?.maxFillMm,
                },
                voidFillPct,
                packingToleranceMm,
                diagonal,
                extraProtectionExcludedBoxes,
            });
        });
        if (remainderFitsAssigned) {
            return { boxes: [], skippedSkus: [], diagonalSkus: [], fits: true };
        }
        return recommendBoxes({
            items: remainingItems,
            boxes: warehouseBoxes,
            voidFillPct,
            packingToleranceMm,
            considerStock: stockAwareRecommendation,
            diagonal,
            extraProtectionExcludedBoxes,
            shippingTiers,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order.items, boxes, warehouseBoxes, voidFillPct, packingToleranceMm, stockAwareRecommendation, diagonalEnabled, diagonalToleranceMm, diagonalBagToleranceMm, extraProtectionExcludedBoxes, shippingTiers, fragileOverride]);

    // ── Box-recommendation telemetry ─────────────────────────────────
    // Every box scan made while a recommendation was showing is logged:
    // accepted (scanned the suggested box) or overridden. Overrides pop
    // a one-tap reason modal; item/box scanning is never blocked — the
    // scanned box is already assigned by the time the modal appears —
    // but order COMPLETION holds until the question is answered (see the
    // auto-book effect), otherwise a final-scan override completes and
    // navigates away before the operator can answer.
    // How to undo the box choice if the operator dismisses the override
    // modal (mis-scan): restore the affected tab to its pre-scan state —
    // prevBox null means the tab didn't exist and gets removed.
    type OverrideRevert = { boxId: number; prevBox: DispatchBox | null };
    const [overridePrompt, setOverridePrompt] = useState<{
        recommendedBarcode: string;
        recommendedName: string;
        scannedBarcode: string;
        scannedName: string;
        // The overridden recommendation relied on a diagonal placement —
        // carried into the telemetry row when the operator picks a reason.
        diagonal: boolean;
        revert: OverrideRevert | null;
    } | null>(null);
    const recommendationRef = useRef(recommendation);
    recommendationRef.current = recommendation;
    // Ref mirror of overridePrompt, ALSO set synchronously the moment an
    // override is detected (before the state lands on the next render).
    // The auto-book effect reads this instead of the state: it can run in
    // the SAME commit as the deferred outcome evaluation, and booking
    // must never start while the why-did-you-override question is up.
    const overridePromptRef = useRef<typeof overridePrompt>(null);
    overridePromptRef.current = overridePrompt;

    const postRecommendationLog = (entry: {
        recommendedBarcode: string;
        recommendedName: string;
        scannedBarcode: string;
        scannedName: string;
        accepted: boolean;
        reason: string | null;
        diagonal: boolean;
    }) => {
        const user = getCurrentUser();
        apiFetch(`/dispatch/recommendation-log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                warehouse: deskInfo.warehouse,
                desk_id: deskInfo.deskId,
                order_id: order.order_id,
                order_number: order.order_number,
                user_uid: user.uid,
                user_name: user.name,
                recommended_barcode: entry.recommendedBarcode,
                recommended_name: entry.recommendedName,
                scanned_barcode: entry.scannedBarcode,
                scanned_name: entry.scannedName,
                accepted: entry.accepted,
                reason: entry.reason,
                // TRUE when the shown recommendation relied on a diagonal
                // placement — lets us track acceptance of the feature.
                diagonal: entry.diagonal,
            }),
        }).catch(() => { /* telemetry only — never surface to the operator */ });
    };

    // Compare a chosen box against a recommendation snapshot: log the
    // acceptance, or pop the override-reason modal. `revert` is the undo
    // used if the operator dismisses the modal (mis-scan) — the wrong
    // box only STAYS if they pick a reason.
    const evaluateBoxOutcome = (
        rec: { fits: boolean; boxes: Array<{ barcode: string; name: string }>; diagonalSkus?: string[] },
        scanned: { barcode: string; name: string },
        revert: OverrideRevert | null = null,
    ) => {
        if (!rec.fits || rec.boxes.length === 0) return;
        const recommended = rec.boxes[0];
        const diagonal = (rec.diagonalSkus?.length ?? 0) > 0;
        if (recommended.barcode === scanned.barcode) {
            postRecommendationLog({
                recommendedBarcode: recommended.barcode,
                recommendedName: recommended.name,
                scannedBarcode: scanned.barcode,
                scannedName: scanned.name,
                accepted: true,
                reason: null,
                diagonal,
            });
        } else {
            const prompt = {
                recommendedBarcode: recommended.barcode,
                recommendedName: recommended.name,
                scannedBarcode: scanned.barcode,
                scannedName: scanned.name,
                diagonal,
                revert,
            };
            // Sync ref first — the auto-book effect may run before the
            // state update renders, and it must see the pending question.
            overridePromptRef.current = prompt;
            setOverridePrompt(prompt);
        }
    };

    // A box scanned before the warehouse catalogue / settings fetches
    // settle can't be evaluated yet — the recommendation memo computes
    // "nothing fits" against an empty box list. Park the scan here and
    // the effect below evaluates it the moment the data lands, so an
    // operator can't dodge the override modal by scanning their box in
    // the first seconds of an order (root cause of JLI_0804_4's missing
    // telemetry row, 2026-08-05).
    const pendingOutcomeRef = useRef<{
        scanned: { barcode: string; name: string };
        revert: OverrideRevert | null;
    } | null>(null);

    // Latest-ref so handleBoxScan's memoised closure never sees a stale
    // recommendation.
    const recordBoxScanOutcome = (
        scanned: { barcode: string; name: string },
        revert: OverrideRevert | null = null,
    ) => {
        if (!boxesLoaded || !settingsLoaded) {
            pendingOutcomeRef.current = { scanned, revert };
            return;
        }
        if (!boxRecommendationEnabled) return;
        evaluateBoxOutcome(recommendationRef.current, scanned, revert);
    };
    const recordBoxScanOutcomeRef = useRef(recordBoxScanOutcome);
    recordBoxScanOutcomeRef.current = recordBoxScanOutcome;

    useEffect(() => {
        if (!boxesLoaded || !settingsLoaded) return;
        const pending = pendingOutcomeRef.current;
        if (!pending) return;
        pendingOutcomeRef.current = null;
        if (!boxRecommendationEnabled) return;
        // The deferred scan was necessarily the order's FIRST box (it beat
        // the data fetches), so compare against the whole-order
        // recommendation — the memo has already pivoted to "remaining
        // items" now that the box is assigned, which isn't the comparison
        // the operator made.
        const rec = recommendBoxes({
            items: order.items.map(i => ({
                sku: i.sku,
                quantity: Math.max(0, i.quantity - (resolvedCount(i) - shippedCount(i))),
                lengthCm: i.length_cm ?? null,
                widthCm: i.width_cm ?? null,
                heightCm: i.height_cm ?? null,
                components: recommenderComponents(i),
                extraProtection: fragileOverride || i.extra_protection === true,
                soft: i.soft_item === true,
                weightGrams: i.weight_grams,
            })),
            boxes: warehouseBoxes,
            voidFillPct,
            packingToleranceMm,
            considerStock: stockAwareRecommendation,
            diagonal: { enabled: diagonalEnabled, toleranceMm: diagonalToleranceMm, bagToleranceMm: diagonalBagToleranceMm },
            // Must mirror the live recommendation exactly, or the
            // telemetry records an override against a suggestion the
            // operator was never shown.
            extraProtectionExcludedBoxes,
            shippingTiers,
        });
        evaluateBoxOutcome(rec, pending.scanned, pending.revert);
    // evaluateBoxOutcome/resolvedCount are stable per render; the trigger
    // is the data fetches settling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boxesLoaded, settingsLoaded, boxRecommendationEnabled, warehouseBoxes, voidFillPct, packingToleranceMm, stockAwareRecommendation, diagonalEnabled, diagonalToleranceMm, diagonalBagToleranceMm, extraProtectionExcludedBoxes, shippingTiers, order.items, fragileOverride]);

    // Derive a single row status from the per-unit counts. "scanned" is used
    // whenever the row has at least one shipped unit so it correctly lands in
    // its assigned box; "missing"/"damaged" are reserved for fully-flagged rows.
    const deriveScanStatus = (item: DispatchOrderItem): DispatchOrderItem["scanStatus"] => {
        if (!isItemResolved(item)) return "pending";
        if (shippedCount(item) > 0) return "scanned";
        if (item.damagedCount > 0) return "damaged";
        return "missing";
    };

    // Count progress
    const totalRequired = order.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalResolved = order.items.reduce((sum, item) => sum + resolvedCount(item), 0);
    const allResolved = order.items.every(isItemResolved);
    const totalWeight = order.items.reduce(
        (sum, item) => sum + (item.weight_grams * shippedCount(item)),
        0
    );

    // A line that has just been finished keeps its place for a moment,
    // flashing green, before it drops to the finished group at the bottom
    // (RESOLVED_HOLD_MS). Detected from the items themselves, so a scan,
    // a Missing/Damaged mark and a flagged-pick collect all behave alike.
    const [heldIds, setHeldIds] = useState<ReadonlySet<string>>(() => new Set());
    const resolvedIdsRef = useRef<{ orderId: string; ids: Set<string> } | null>(null);
    const holdTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    useEffect(() => () => holdTimersRef.current.forEach(clearTimeout), []);
    useEffect(() => {
        const ids = new Set(order.items.filter(isItemResolved).map(i => i.item_id));
        const prev = resolvedIdsRef.current;
        resolvedIdsRef.current = { orderId: order.order_id, ids };
        if (!prev) return;
        if (prev.orderId !== order.order_id) {
            setHeldIds(h => (h.size ? new Set() : h));
            return;
        }
        const fresh = Array.from(ids).filter(id => !prev.ids.has(id));
        if (fresh.length === 0) return;
        setHeldIds(h => new Set([...Array.from(h), ...fresh]));
        holdTimersRef.current.push(setTimeout(() => {
            setHeldIds(h => {
                const next = new Set(h);
                fresh.forEach(id => next.delete(id));
                return next;
            });
        }, RESOLVED_HOLD_MS));
    }, [order]); // eslint-disable-line react-hooks/exhaustive-deps
    const sortedItems = sortForPacking(order.items, isItemResolved, heldIds);

    // Barcode search: matching lines move to the top, in yellow, with the
    // matching characters marked (packSearch.ts).
    const searchTerm = searchOpen ? searchQuery.trim() : "";
    const searchHits = new Map<string, SearchHit>();
    if (searchTerm) {
        for (const item of order.items) {
            const hit = findSearchHit(item, searchTerm);
            if (hit) searchHits.set(item.item_id, hit);
        }
    }
    const listItems = searchTerm ? hitsFirst(sortedItems, i => searchHits.has(i.item_id)) : sortedItems;
    // Each new search starts at the top of the list, where its matches are.
    useEffect(() => {
        if (searchTerm && listRef.current) listRef.current.scrollTop = 0;
    }, [searchTerm]);

    // "More below: N" — lines wholly or partly under the list's visible
    // edge. Re-measured on scroll, on resize and whenever the lines change.
    const measureMoreBelow = useCallback(() => {
        const list = listRef.current;
        if (!list) return;
        const rows = Array.from(list.querySelectorAll<HTMLElement>(".dispatch-item-row"));
        setMoreBelow(countRowsBelow(rows.map(r => r.offsetTop + r.offsetHeight), list.scrollTop + list.clientHeight));
    }, []);
    useLayoutEffect(() => { measureMoreBelow(); });
    useEffect(() => {
        const list = listRef.current;
        if (!list || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => measureMoreBelow());
        observer.observe(list);
        return () => observer.disconnect();
    }, [measureMoreBelow]);
    const scrollListDown = () => {
        const list = listRef.current;
        if (!list) return;
        const by = Math.max(list.clientHeight * 0.8, 80);
        if (typeof list.scrollBy === "function") list.scrollBy({ top: by, behavior: "smooth" });
        else list.scrollTop += by;
    };

    // Every box must have at least one shipped item assigned to it
    const allBoxesHaveItems = boxes.every(box =>
        order.items.some(i => i.assignedBoxes.includes(box.id))
    );
    // At least one box must have been "assigned" — either scan-assigned
    // (boxRef set) or filled in via the custom-box modal (dimensions set
    // manually). Auto-created tabs that are still empty placeholders don't
    // count, so we don't auto-book the courier or enable Complete until
    // the operator has either scanned a box barcode or entered custom
    // dimensions.
    const hasScannedBox = boxes.some(isBoxAssigned);
    // Flagged Picks (2026-10-08): a unit still being fetched from high
    // racking means this pack would ship short for nothing, so Complete
    // (and the auto-book that keys off canComplete) waits for it. The BE
    // refuses too (409 flagged_pick_pending). The leader's resolve flow is
    // the one place a ship without it is allowed (ship_short).
    const flaggedPickBlocked = !resolveMode && hasPendingFlaggedPick(order);
    const canComplete = allResolved && allBoxesHaveItems && hasScannedBox && order.items.some(i => shippedCount(i) > 0) && !flaggedPickBlocked;
    // Pre-load: count how many individual scans remain across all items
    const pendingScans = order.items.reduce(
        (sum, item) => sum + Math.max(0, item.quantity - resolvedCount(item)),
        0
    );
    const preloadResultRef = useRef<CourierBookingResult | null>(null);
    const preloadingRef = useRef(false);

    // Own-label detection: an order with an uploaded shipping-label attachment
    // (kind='shipping_label') ships on ITS OWN label — the courier booking is
    // skipped entirely so we're never charged for an APC label we won't print.
    // Fetched once on open; the auto-book effect reads the ref so it can decide
    // in the same commit as completion. null = not yet resolved / none.
    const ownLabelRef = useRef<{ attachment_id: string; filename: string } | null>(null);
    // Gate the auto-book on this so a fast single-item completion can't book
    // (and charge) a courier BEFORE we know the order ships on its own label.
    const [ownLabelLoaded, setOwnLabelLoaded] = useState(false);
    useEffect(() => {
        let alive = true;
        ownLabelRef.current = null;
        setOwnLabelLoaded(false);
        (async () => {
            try {
                const r = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/attachments`, {});
                if (!alive) return;
                if (r.ok) {
                    const data = await r.json();
                    if (!alive) return;
                    const label = (Array.isArray(data.attachments) ? data.attachments : [])
                        .find((a: { kind?: string }) => a.kind === "shipping_label");
                    ownLabelRef.current = label
                        ? { attachment_id: label.attachment_id, filename: label.filename }
                        : null;
                }
            } catch { /* leave as null — falls back to courier booking */ }
            finally { if (alive) setOwnLabelLoaded(true); }
        })();
        return () => { alive = false; };
    }, [order.order_id]);

    // Collection order (delivery_type = collection): staff purchase picked up
    // at the warehouse counter. Never a carrier — the BE's bookOrder refuses
    // it (code 'collection') and the close must carry collection:true.
    const isCollection = (order.delivery_type ?? "").toLowerCase() === "collection";

    // Build the collection completion result: no booking_id, collection flag
    // set so Confirm Shipment prints the COLLECTION label (order-number
    // barcode) and closes with collection:true. Parcels mirror the box set;
    // the BE pins carrier/service and synthesises the tracking numbers.
    const buildCollectionResult = (): CourierBookingResult => ({
        parcels: boxes.map((b, i) => ({
            carrier: "Collection",
            tracking_number: "",
            service: "collection",
            items: boxItems(b.id),
            box_position: i + 1,
        })),
        carrier: "Collection",
        service: "collection",
        collection: true,
    });

    // Build the own-label completion result: no booking_id (nothing booked),
    // own_label flags set so Confirm Shipment prints the attachment. Parcels
    // mirror the box set with no courier tracking (there is no courier).
    const buildOwnLabelResult = (label: { attachment_id: string; filename: string }): CourierBookingResult => ({
        parcels: boxes.map((b, i) => ({
            carrier: "own_label",
            tracking_number: "",
            service: "",
            items: boxItems(b.id),
            box_position: i + 1,
        })),
        carrier: "own_label",
        service: "",
        own_label: true,
        own_label_attachment_id: label.attachment_id,
        own_label_filename: label.filename,
    });

    // Pre-load courier when 1 scan away from completion. DEMO only:
    // real carrier bookings (APC) must run on the FINAL box/weight set,
    // so they only fire once the last item is scanned (per Jack,
    // 2026-08-05 — the rules pick the most accurate + economical
    // service from the finished parcels).
    useEffect(() => {
        // Never for a collection order: there is no courier to pre-load.
        if (DEMO_MODE && !isCollection && pendingScans === 1 && !preloadResultRef.current && !preloadingRef.current) {
            preloadingRef.current = true;
            preloadCourier();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingScans, DEMO_MODE]);

    // Best-effort stock decrement once an order is locked in. Boxes that
    // weren't scan-assigned (boxRef === null) are skipped — we only have a
    // record of consumption for the ones the operator actually scanned.
    // Fire-and-forget so a slow BE doesn't delay the courier-booking UX.
    const decrementBoxStocks = useCallback(() => {
        if (readOnly) return;
        for (const b of boxes) {
            if (!b.boxRef) continue;
            apiFetch(`/dispatch-admin/boxes/decrement`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: b.boxRef, units: 1 }),
            }).catch(() => { /* tracking-only, no UX surface */ });
        }
    }, [boxes]);

    // Auto-book courier when all items resolved and all boxes have items —
    // but NEVER while the override-reason modal is open, and not before
    // the boxes/settings fetches settle. When the order's LAST scan was an
    // overriding box scan, booking used to start concurrently with the
    // modal, and the completion navigation tore the question down
    // unanswered — no reason, no telemetry row, and the operator barely
    // saw a flash (JLI_0804_15, 2026-08-05: P1 scanned against a P2
    // recommendation). Picking a reason closes the modal and booking
    // resumes via the overridePrompt dep; dismissing reverts the box
    // choice, which withdraws canComplete until a box is scanned again.
    // The loaded gates make a parked early scan (pendingOutcomeRef) get
    // judged — and its modal shown — BEFORE the screen can complete and
    // unmount; the deferred-evaluation effect above runs first in the
    // same commit and sets overridePromptRef synchronously.
    const hasAutoBooked = useRef(suppressAutoModal || false);
    useEffect(() => {
        if (canComplete && !hasAutoBooked.current
            && overridePromptRef.current === null
            && boxesLoaded && settingsLoaded && ownLabelLoaded) {
            hasAutoBooked.current = true;
            playComplete();
            // Collection order → handed over at the counter, never a carrier.
            // Checked FIRST: it beats an uploaded label and any pre-loaded
            // demo result, and never goes near /carriers/bookings (the BE
            // refuses it there anyway, code 'collection'). Confirm Shipment
            // prints the collection label and closes with collection:true.
            if (isCollection) {
                playSuccess();
                decrementBoxStocks();
                onComplete(buildCollectionResult(), boxes);
            } else if (ownLabelRef.current) {
            // Own-label order → ship on its uploaded label. Skip the courier
            // booking ENTIRELY (no /carriers/bookings call, no APC charge) and
            // hand Confirm Shipment an own-label result to print the PDF.
                playSuccess();
                decrementBoxStocks();
                onComplete(buildOwnLabelResult(ownLabelRef.current), boxes);
            } else if (preloadResultRef.current) {
                // Use pre-loaded result if available, otherwise book now
                playSuccess();
                decrementBoxStocks();
                onComplete(preloadResultRef.current, boxes);
            } else {
                bookCourier();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canComplete, overridePrompt, boxesLoaded, settingsLoaded, ownLabelLoaded]);

    const buildCourierPayload = () => {
        const boxCount = boxes.length;
        const shippedItems = order.items.filter(i => shippedCount(i) > 0);
        return { boxCount, shippedItems };
    };

    // Per-parcel item split from the packer's real box assignments —
    // assignedBoxes carries one entry per shipped unit. Feeds the parcels
    // recorded against the order (APC bookings, own-label, demo courier)
    // so each parcel records what is physically in its box (it used to
    // dump every item on parcel 1, leaving the rest recorded as empty).
    //
    // Merged BY SKU, not by order line. Two lines can carry the same SKU
    // (a consolidation master lists each child's line; a re-order on one
    // order) and the backend keys parcel contents on (tracking, sku) —
    // one entry per line for the same box overwrote the first with the
    // second, so CON170 recorded 1 unit of each doubled SKU instead of 2
    // and the admin view read the second line as "Not Shipped".
    const boxItems = (boxId: number | undefined) => {
        if (boxId == null) return [];
        const bySku = new Map<string, number>();
        for (const i of order.items) {
            const units = i.assignedBoxes.filter(b => b === boxId).length;
            if (units > 0) bySku.set(i.sku, (bySku.get(i.sku) ?? 0) + units);
        }
        return Array.from(bySku, ([sku, quantity]) => ({ sku, quantity }));
    };
    const demoBoxItems = boxItems;

    const preloadCourier = async () => {
        // DEMO only (the effect above gates on DEMO_MODE) — fabricate the
        // legacy demo result so demo benches keep their instant flow.
        const { boxCount } = buildCourierPayload();
        const parcels = Array.from({ length: boxCount }, (_, i) => ({
            carrier: totalWeight > 5000 ? "Yodel" : "Royal Mail",
            tracking_number: `YOD${Date.now().toString().slice(-6)}${i + 1}`,
            tracking_url: `https://track.yodel.co.uk/YOD${Date.now().toString().slice(-6)}${i + 1}`,
            service: totalWeight > 5000 ? "Next Day" : "48hr Tracked",
            items: demoBoxItems(boxes[i]?.id),
        }));
        preloadResultRef.current = { parcels, carrier: parcels[0].carrier, service: parcels[0].service };
        preloadingRef.current = false;
    };

    // ── Carrier booking (APC) ──────────────────────────────────────────
    // Runs when the LAST item is scanned: POST /carriers/bookings with the
    // final box set, then poll for the label. Failure (or no matching
    // shipping rule) BLOCKS the order behind the DispatchBookingStatus
    // overlay — nothing ships without a booked label.
    const [carrierPanel, setCarrierPanel] = useState<CarrierPanelState | null>(null);
    const [carrierRetrying, setCarrierRetrying] = useState(false);
    const [carrierRebooking, setCarrierRebooking] = useState(false);
    const carrierBookingIdRef = useRef<number | null>(null);
    // The label already booked for this order, if the bench knows of one.
    // Seeded from the prop, re-seeded when the page hands a different one,
    // and set from a booking_box_mismatch refusal. Cleared once a booking
    // succeeds.
    const [liveBookingState, setLiveBookingState] = useState<{ id: number; pieces: number; carrierName?: string | null } | null>(liveBooking ?? null);
    useEffect(() => {
        setLiveBookingState(liveBooking ?? null);
    }, [liveBooking?.id, liveBooking?.pieces]); // eslint-disable-line react-hooks/exhaustive-deps

    // Contents weight per box: the units the packer actually assigned to it
    // (assignedBoxes carries one entry per shipped unit) at their catalogue
    // weight — or, for an own-packaging container weighed at the bench, the
    // reading, while it still holds the units it was weighed with.
    const contentsWeightFor = (boxId: number | undefined) => {
        if (boxId == null) return 0;
        const weighed = measuredContentsGrams(boxes.find(b => b.id === boxId));
        if (weighed != null) return weighed;
        return order.items.reduce(
            (sum, i) => sum + (i.weight_grams ?? 0) * i.assignedBoxes.filter(b => b === boxId).length,
            0,
        );
    };

    // Over-weight chip per tab: the box's own optional limit (Settings >
    // Boxes) or the carrier ceiling from the shipping tiers, whichever is
    // tighter. A warning, not a block — the packer decides how to split.
    const carrierMaxGrams = carrierMaxParcelGrams(shippingTiers);
    const weightWarningFor = (box: { id: number; boxRef?: string | null }) =>
        boxWeightWarning({
            contentsGrams: contentsWeightFor(box.id),
            boxMaxGrams: box.boxRef ? warehouseBoxes.find((b) => b.barcode === box.boxRef)?.maxWeightGrams : null,
            carrierMaxGrams,
        });
    const weightWarningText = (w: WeightWarning) => t(
        w.kind === "box_max" ? "pack_over_box_max" : "pack_over_carrier_max",
        { limit: (w.limitGrams / 1000).toFixed(1), weight: (w.weightGrams / 1000).toFixed(1) },
    );

 const buildCarrierBoxes = () => {
    // 1. Compute raw weights per box (already in grams)
    const rawBoxes = boxes.map((b, idx) => ({
        box_position: idx + 1,
        barcode: b.boxRef ?? null,
        name: b.boxRef ? (b.boxName ?? null) : null,
        rawWeight: Math.max(1, Math.round(contentsWeightFor(b.id))),
        // Packaging weight of the box itself — InPost declare and band
        // on the measured parcel, so the BE adds it for them; APC stays
        // contents-only.
        tare_grams: b.boxWeightGrams ?? null,
        length_mm: b.boxLengthMm ?? null,
        width_mm: b.boxWidthMm ?? null,
        height_mm: b.boxHeightMm ?? null,
        units: order.items.reduce(
            (sum, i) => sum + i.assignedBoxes.filter(x => x === b.id).length,
            0,
        ),
    }));

    // 2. The bench sends the true contents weight per box. Any
    //    carrier-specific declared-weight convention is applied on the
    //    BE once the shipping rules have chosen the carrier (the bench
    //    cannot know which carrier will book at this point).
    const adjustedBoxes = rawBoxes.map((b) => ({ ...b, weight_grams: b.rawWeight }));

    // 3. Return the final array in the shape expected by the API
    return adjustedBoxes.map(({ box_position, barcode, name, weight_grams, tare_grams, length_mm, width_mm, height_mm, units }) => ({
        box_position,
        barcode,
        name,
        weight_grams,
        tare_grams,
        length_mm,
        width_mm,
        height_mm,
        units,
    }));
};

    // APC's consignee-facing tracking page ("APC PINPOINT"). Verified
    // live 2026-08-13 against consignment 2026080722733780045081.
    //
    // The OLD apc-overnight.com/track-parcel.php URL is dead: APC
    // rebranded to apc.co.uk and every apc-overnight.com path now
    // 301s to the bare homepage, silently dropping the query string.
    // The apcchoice.* subdomain is the surviving deep-linkable page —
    // apc.co.uk's own tracker is an htmx POST, so it can't be linked to.
    //
    // Takes the 22-digit CONSIGNMENT id. A per-item tracking number is
    // that id plus a 3-digit item suffix, and APC rejects the 25-digit
    // form outright ("invalid"), so always trim back to 22.
    //
    // No postcode parameter: APC ignores one if supplied. The parcel
    // journey renders without it — the recipient is only asked for a
    // postcode if they want proof-of-delivery or APC Choice options.
    const apcTrackingUrl = (waybill: string) => {
        const digits = waybill.replace(/\D/g, "");
        const id = digits.length === 25 ? digits.slice(0, 22) : digits;
        return `https://apcchoice.apc-overnight.com/track-parcel?id=${encodeURIComponent(id)}`;
    };

    const completeWithCarrierBooking = (bookingData: {
        id: number; waybill: string | null; serviceCode: string; merged?: boolean;
        carrierName?: string | null; carrierCode?: string | null;
        pieces: { piece: number; tracking_number: string; waybill?: string | null; tracking_url?: string | null }[];
    }) => {
        // The booking names its carrier (APC / InPost) and each piece
        // carries its own public tracking link since migration 185; an
        // older BE without either is APC by construction.
        const carrierName = bookingData.carrierName || "APC";
        const result: CourierBookingResult = {
            booking_id: bookingData.id,
            waybill: bookingData.waybill ?? undefined,
            merged: bookingData.merged === true,
            carrier: carrierName,
            service: bookingData.serviceCode,
            parcels: bookingData.pieces.map((p, idx) => {
                // Split bookings carry per-piece waybills — each parcel
                // tracks against ITS consignment.
                const waybill = p.waybill ?? bookingData.waybill;
                const trackingUrl = p.tracking_url
                    ?? (carrierName === "APC" && waybill ? apcTrackingUrl(waybill) : undefined);
                return {
                    carrier: carrierName,
                    service: bookingData.serviceCode,
                    tracking_number: p.tracking_number,
                    tracking_url: trackingUrl ?? undefined,
                    box_position: p.piece ?? idx + 1,
                    items: demoBoxItems(boxes[idx]?.id),
                };
            }),
        };
        setCarrierPanel(null);
        setLiveBookingState(null);
        playSuccess();
        decrementBoxStocks();
        onComplete(result, boxes);
    };

    // Poll POST /carriers/bookings/:id/labels until APC has generated
    // the ZPL. Live measurement (2026-08-05): labels are available
    // immediately — first poll 520ms after create succeeded — so poll
    // at once, then every 2s as the fallback for slow days.
    //
    // Only a 502 (APC unreachable/rejected mid-flight) is worth
    // re-polling. Anything else is the BE telling us this booking will
    // NEVER produce a label — most often 409 wrong_status because the
    // booking is being cancelled. Reporting those as "label_timeout"
    // sent everyone hunting APC latency for a fault that was entirely
    // ours (JLI_0805_4, 2026-08-06), so pass the real code through.
    // label_timeout now means only what it says: the loop ran out.
    const pollCarrierLabels = async (bookingId: number) => {
        setCarrierPanel({ state: "label_pending" });
        for (let attempt = 0; attempt < 12; attempt++) {
            if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
            try {
                // purpose "poll": waiting for the label is not printing it,
                // so the BE keeps this booking replaceable if the packer
                // changes boxes before Confirm Shipment prints.
                const res = await apiFetch(`/carriers/bookings/${bookingId}/labels`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ format: "ZPL", purpose: "poll" }),
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok && data.status === "label_ready") {
                    completeWithCarrierBooking({
                        id: data.booking.id,
                        waybill: data.booking.waybill,
                        serviceCode: data.booking.serviceCode,
                        merged: data.booking.merged === true,
                        carrierName: data.booking.carrierName ?? null,
                        carrierCode: data.booking.carrierCode ?? null,
                        pieces: data.booking.pieces ?? [],
                    });
                    return;
                }
                if (!res.ok && res.status !== 502) {
                    playError();
                    setCarrierPanel({
                        state: "failed",
                        // wrong_status carries the booking's actual status —
                        // 'cancel_pending' is its own message, since that one
                        // is resolvable (wait for the void) rather than fatal.
                        code: data.status === "cancel_pending"
                            ? "booking_cancelling"
                            : data.code ?? "label_fetch_failed",
                        error: data.error ?? data.message ?? null,
                    });
                    return;
                }
            } catch { /* transient — keep polling */ }
        }
        playError();
        setCarrierPanel({ state: "failed", code: "label_timeout" });
    };

    // Returns "booked" (flow continues async), "fallback" (warehouse has
    // no shipping rules — legacy/demo path applies) or "blocked".
    const bookCarrier = async (): Promise<"booked" | "fallback" | "blocked"> => {
        if (readOnly) return "blocked";
        setCarrierPanel({ state: "booking" });
        // The saved desk must not be behind the booking (desk session).
        try { await onBeforeBook?.(); } catch { /* saving is best effort */ }
        const user = getCurrentUser();
        try {
            const res = await apiFetch(`/carriers/bookings`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    order_id: order.order_id,
                    boxes: buildCarrierBoxes(),
                    booked_by_uid: user.uid,
                    booked_by_name: user.name,
                }),
            });
            if (res.status === 401) {
                logoutProcessor();
                navigate("/login");
                return "blocked";
            }
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                if (!data.booking?.id) {
                    // Malformed 200 — treat as unreachable rather than
                    // spinning on a booking that doesn't exist.
                    playError();
                    setCarrierPanel({ state: "failed", code: "apc_unreachable", error: t("server_error") });
                    return "blocked";
                }
                carrierBookingIdRef.current = data.booking?.id ?? null;
                // Already label_ready (bench refresh / idempotent re-entry)?
                if (data.booking?.status === "label_ready" || data.booking?.status === "completed") {
                    completeWithCarrierBooking({
                        id: data.booking.id,
                        waybill: data.booking.waybill,
                        serviceCode: data.booking.serviceCode,
                        merged: data.booking.merged === true,
                        carrierName: data.booking.carrierName ?? null,
                        carrierCode: data.booking.carrierCode ?? null,
                        pieces: data.booking.pieces ?? [],
                    });
                    return "booked";
                }
                pollCarrierLabels(data.booking.id);
                return "booked";
            }
            // No rules for this warehouse → carrier flow doesn't apply.
            if (res.status === 422 && data.code === "no_rules") {
                setCarrierPanel(null);
                return "fallback";
            }
            // Demo orders may not exist BE-side / not be at dispatch —
            // demo benches fall back rather than block.
            if (DEMO_MODE && (res.status === 404 || (res.status === 409 && data.code === "order_not_dispatch"))) {
                setCarrierPanel(null);
                return "fallback";
            }
            carrierBookingIdRef.current = data.booking?.id ?? null;
            // The refusal names the live label, so the bench now knows the
            // order is booked for a different number of boxes: warn on the
            // box bar and make the empty-box toast say what Complete does.
            if (data.code === "booking_box_mismatch" && data.booking?.id) {
                setLiveBookingState({
                    id: data.booking.id,
                    pieces: Number(data.booked_pieces) || 0,
                    carrierName: data.carrier ?? data.booking?.carrierName ?? null,
                });
            }
            playError();
            setCarrierPanel({
                state: "failed",
                code: data.code ?? "apc_unreachable",
                // booking_box_mismatch already says everything in its
                // heading, and its message is ours, not the carrier’s —
                // don’t repeat it under "APC said".
                error: data.code === "booking_box_mismatch"
                    ? null
                    : data.error ?? data.message ?? null,
                apcErrors: data.carrier_errors ?? data.apc_errors ?? [],
                carrierName: data.carrier ?? data.booking?.carrierName ?? null,
                country: data.country ?? null,
                allowedCountries: Array.isArray(data.allowed) ? data.allowed : [],
                trace: data.trace ?? [],
                ruleName: data.ruleName ?? null,
                bookedPieces: data.booked_pieces ?? null,
                packedBoxes: data.submitted_boxes ?? null,
                declaredKg: data.declared_kg ?? null,
                packedKg: data.submitted_kg ?? null,
            });
            return "blocked";
        } catch {
            playError();
            setCarrierPanel({ state: "failed", code: "apc_unreachable", error: t("server_error") });
            return "blocked";
        }
    };

    // Dismiss the failure overlay. The order stays unbooked and
    // incomplete — the Complete button re-runs the booking — so the
    // packer can go and fix whatever the carrier objected to instead of
    // being stuck on a modal whose only button retries the same failure.
    // hasAutoBooked stays TRUE deliberately: re-arming it would auto-book
    // again the moment React re-evaluated canComplete, putting the same
    // modal straight back up.
    const dismissCarrierPanel = () => setCarrierPanel(null);

    // Correct the delivery address from inside the failure overlay and
    // persist it, so an APC address rejection is fixable at the bench.
    // The address editor otherwise lives on the Confirm Shipment screen,
    // which needs a booked label to reach — i.e. it was unreachable in
    // exactly the case that needs it (2026-08-19).
    const saveDeliveryAddress = async (address: DispatchAddress): Promise<boolean> => {
        if (readOnly) return false;
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ delivery_address: address }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                playError();
                toast.dismiss();
                toast.error(data?.message || t("server_error"));
                return false;
            }
            // Keep the on-screen order in step — the confirm screen reads
            // its address from here once the booking succeeds.
            onUpdateOrder({ ...order, delivery_address: address });
            toast.dismiss();
            toast.success(t("address_updated"));
            return true;
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("server_error"));
            return false;
        }
    };

    // Retry from the blocked overlay. A failed/unknown booking retries via
    // its own endpoint (supersedes + blind-cancels the dead row); a
    // no-match/no-booking failure simply re-runs the booking.
    const retryCarrier = async () => {
        if (readOnly) return;
        // Collection order: nothing to retry against the carrier — close it
        // the collection way (see bookCourier).
        if (isCollection) { await bookCourier(); return; }
        setCarrierRetrying(true);
        try {
            const bookingId = carrierBookingIdRef.current;
            if (bookingId) {
                const user = getCurrentUser();
                const res = await apiFetch(`/carriers/bookings/${bookingId}/retry`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        boxes: buildCarrierBoxes(),
                        booked_by_uid: user.uid,
                        booked_by_name: user.name,
                    }),
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                    carrierBookingIdRef.current = data.booking?.id ?? null;
                    if (data.booking?.status === "label_ready" || data.booking?.status === "completed") {
                        completeWithCarrierBooking({
                            id: data.booking.id,
                            waybill: data.booking.waybill,
                            serviceCode: data.booking.serviceCode,
                            carrierName: data.booking.carrierName ?? null,
                            carrierCode: data.booking.carrierCode ?? null,
                            pieces: data.booking.pieces ?? [],
                        });
                    } else {
                        pollCarrierLabels(data.booking.id);
                    }
                    return;
                }
                // wrong_status (e.g. booking actually succeeded and labels
                // exist) → fall through to a fresh bookCarrier which will
                // reuse the active booking idempotently.
            }
            await bookCarrier();
        } finally {
            setCarrierRetrying(false);
        }
    };

    // Void the live label and book again for the boxes actually on the
    // bench (booking_box_mismatch only; the panel has already asked the
    // packer to confirm). The BE voids first and refuses to book if the
    // carrier will not let go of the old label, so a failure here leaves
    // the order exactly as it was.
    const rebookCarrier = async () => {
        if (readOnly || carrierRebooking) return;
        const bookingId = carrierBookingIdRef.current ?? liveBookingState?.id ?? null;
        if (!bookingId) return;
        setCarrierRebooking(true);
        try {
            const user = getCurrentUser();
            const res = await apiFetch(`/carriers/bookings/${bookingId}/rebook`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    boxes: buildCarrierBoxes(),
                    booked_by_uid: user.uid,
                    booked_by_name: user.name,
                }),
            });
            if (res.status === 401) {
                logoutProcessor();
                navigate("/login");
                return;
            }
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.booking?.id) {
                carrierBookingIdRef.current = data.booking.id;
                setLiveBookingState(null);
                if (data.booking.status === "label_ready" || data.booking.status === "completed") {
                    completeWithCarrierBooking({
                        id: data.booking.id,
                        waybill: data.booking.waybill,
                        serviceCode: data.booking.serviceCode,
                        merged: data.booking.merged === true,
                        carrierName: data.booking.carrierName ?? null,
                        carrierCode: data.booking.carrierCode ?? null,
                        pieces: data.booking.pieces ?? [],
                    });
                } else {
                    pollCarrierLabels(data.booking.id);
                }
                return;
            }
            playError();
            if (res.ok) {
                // Malformed 200 - no booking to poll.
                setCarrierPanel({ state: "failed", code: "apc_unreachable", error: t("server_error") });
                return;
            }
            const code: string = data.code ?? "apc_unreachable";
            setCarrierPanel({
                state: "failed",
                code,
                // These headings already say everything; the BE message is
                // ours, not the carrier's - do not repeat it under "Carrier said".
                error: ["already_shipped", "shared_consignment", "cancel_window_closed", "booking_box_mismatch"].includes(code)
                    ? null
                    : data.error ?? data.message ?? null,
                apcErrors: data.carrier_errors ?? data.apc_errors ?? [],
                carrierName: data.carrier ?? data.booking?.carrierName ?? liveBookingState?.carrierName ?? null,
                cutoff: data.cutoff ?? null,
                reason: data.reason ?? null,
                bookedPieces: data.booked_pieces ?? null,
                packedBoxes: data.submitted_boxes ?? null,
                declaredKg: data.declared_kg ?? null,
                packedKg: data.submitted_kg ?? null,
            });
        } catch {
            playError();
            setCarrierPanel({ state: "failed", code: "apc_unreachable", error: t("server_error") });
        } finally {
            setCarrierRebooking(false);
        }
    };

    // Complete-blocked message for empty boxes. With a label already booked
    // for a different parcel count, say what completing will do instead of
    // only "scan items into it".
    const emptyBoxesMessage = () => {
        const emptyIds = boxes
            .filter(box => !order.items.some(i => i.assignedBoxes.includes(box.id)))
            .map(b => b.id)
            .join(", ");
        return liveBookingState
            ? t("empty_boxes_warning_rebook", { boxes: emptyIds, packed: String(boxes.length) })
            : t("empty_boxes_warning", { boxes: emptyIds });
    };

    const bookCourier = async () => {
        if (booking || readOnly) return;
        // Collection order → handed over at the counter, never a carrier.
        // The auto-book effect already takes this branch, but it fires once
        // per order: a packer who comes back from the ship screen, or adds a
        // box after the last scan, reaches Complete / Retry and landed here,
        // where the BE refused the booking (422 'collection') and the bench
        // showed "Carrier booking failed" with no way through (JEU728864,
        // 2026-10-30). Same exit as the auto path: collection result, close
        // with collection:true.
        if (isCollection) {
            playSuccess();
            decrementBoxStocks();
            onComplete(buildCollectionResult(), boxes);
            return;
        }
        // Own-label order → never book (and never charge) a courier; ship on
        // the uploaded label. Guard here too so EVERY completion path (manual
        // Complete button included), not just the auto-book effect, is covered.
        if (ownLabelRef.current) {
            playSuccess();
            decrementBoxStocks();
            onComplete(buildOwnLabelResult(ownLabelRef.current), boxes);
            return;
        }
        setBooking(true);
        try {
            // Real carrier flow first — it decides whether this warehouse
            // ships via shipping rules. Only a 'no_rules' answer falls
            // through to the demo path.
            const outcome = await bookCarrier();
            if (outcome !== "fallback") return;

            if (DEMO_MODE) {
                const parcels = Array.from({ length: boxes.length }, (_, i) => ({
                    carrier: totalWeight > 5000 ? "Yodel" : "Royal Mail",
                    tracking_number: `YOD${Date.now().toString().slice(-6)}${i + 1}`,
                    tracking_url: `https://track.yodel.co.uk/YOD${Date.now().toString().slice(-6)}${i + 1}`,
                    service: totalWeight > 5000 ? "Next Day" : "48hr Tracked",
                    items: demoBoxItems(boxes[i]?.id),
                }));
                const result: CourierBookingResult = {
                    parcels,
                    carrier: parcels[0].carrier,
                    service: parcels[0].service,
                };
                playSuccess();
                decrementBoxStocks();
                onComplete(result, boxes);
                return;
            }

            // Live bench with no shipping rules configured — fail loud;
            // nothing ships without a booked label.
            playError();
            setCarrierPanel({ state: "failed", code: "no_rules" });
        } finally {
            setBooking(false);
        }
    };

    // Box barcodes follow `<code>BOX<seq>` where the warehouse code is 2-4
    // letters: NPBOX2 (Newport), PDBOX1 (Pembroke Dock), PEMBOX3 (Pembroke).
    // Centralised here so the scan handler can route box scans to the
    // dispatch-admin endpoint before falling back to item lookup. The scan
    // handler ALSO routes any exact match against the fetched warehouse box
    // list, so a box with an unconventional barcode still scans as a box.
    const BOX_BARCODE_RE = /^[A-Z]{2,4}BOX\d+$/i;

    // One reusable toast for a rejected scan, so the catalogue lookup can
    // upgrade the message in place rather than stacking a second toast.
    const UNKNOWN_SCAN_TOAST = "pack-scan-unknown";

    const applyBoxAssignment = (boxId: number, b: { barcode: string; name: string; length_mm: number; width_mm: number; height_mm: number; weight_grams: number }) => {
        logDispatchEvent({ order_pick_type: order.pick_type, event: "box-scan", barcode: b.barcode, order_id: order.order_id, desk_id: deskInfo.deskId, box: boxId, box_name: b.name, result: "assigned" });
        const updated = boxes.map(box => box.id === boxId
            ? {
                ...box,
                boxRef: b.barcode,
                boxName: b.name,
                boxLengthMm: b.length_mm,
                boxWidthMm: b.width_mm,
                boxHeightMm: b.height_mm,
                boxWeightGrams: b.weight_grams,
            }
            : box,
        );
        onUpdateBoxes(updated);
    };

    const handleBoxScan = useCallback(async (val: string) => {
        try {
            // Local-first: the warehouse's full box list (dims, weight,
            // name) is already loaded for the recommender, so a barcode in
            // it validates without the /box/check round trip — box scans
            // become instant instead of costing ~0.5s of network. The BE
            // check remains the fallback for barcodes not in the local
            // list (fetch raced the scan, box added mid-shift).
            let box: { barcode: string; name: string; length_mm: number; width_mm: number; height_mm: number; weight_grams: number };
            const local = warehouseBoxes.find(b => b.barcode === val);
            if (local) {
                box = {
                    barcode: local.barcode,
                    name: local.name,
                    length_mm: local.lengthMm,
                    width_mm: local.widthMm,
                    height_mm: local.heightMm,
                    weight_grams: local.weightGrams,
                };
            } else {
                const res = await apiFetch(`/dispatch-admin/box/check`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // Warehouse-scoped: box NAMES are only unique within a
                    // site, so an unscoped check accepts another site's box
                    // and packs the order against dimensions that aren't on
                    // this bench.
                    body: JSON.stringify({ barcode: val, warehouse: deskInfo.warehouse }),
                });
                if (!res.ok) {
                    playError();
                    toast.dismiss();
                    toast.error(t("server_error"));
                    return;
                }
                const data = await res.json();
                if (!data.valid || !data.box) {
                    const wrongSite = data.reason === "box_wrong_warehouse";
                    playError();
                    toast.dismiss();
                    toast.error(t(wrongSite ? "box_wrong_warehouse" : "box_unknown"));
                    logDispatchEvent({ order_pick_type: order.pick_type, event: "box-scan", barcode: val, order_id: order.order_id, desk_id: deskInfo.deskId, result: wrongSite ? "fail-wrong-warehouse" : "fail-unknown" });
                    return;
                }
                box = data.box;
            }

            // Reassignment takes priority — operator clicked a tab and is
            // now scanning a replacement. Whether we STAY in reassign mode
            // afterwards depends on the tab (see below): an empty one is
            // still being sized, a tab holding items has had its one-shot
            // correction. Reassign also exits on an item scan (handled in
            // handleScanSubmit) or by clicking the tab name again.
            // Scans reach this handler while the "+ Add box" modal is open
            // (the global listener stays live behind it). That modal means
            // "another container", so it suppresses both re-target branches
            // below — same rule as applyCustomBox. Without this, reassign
            // mode (still set from the last box added) silently resized the
            // selected tab instead of adding one.
            const fromAddBoxModal = showAddBoxModal;

            if (!fromAddBoxModal && reassignTargetId != null) {
                const prevBox = boxes.find(b => b.id === reassignTargetId) ?? null;
                applyBoxAssignment(reassignTargetId, box);
                recordBoxScanOutcomeRef.current(
                    { barcode: box.barcode, name: box.name },
                    { boxId: reassignTargetId, prevBox },
                );
                // Reassign is one-shot on a tab that already holds items —
                // exactly what the tab-click handler promises ("the next box
                // scan resizes this tab and exits reassign mode"). It never
                // did: nothing here cleared the target, and the only other
                // exits are a successful ITEM scan, clicking the same tab
                // again, or deleting the box. So an operator who wanted a
                // SECOND box just kept resizing the first one, with no way
                // out that looks like "add a box" — order 337463 scanned
                // PEMBOX10/PEMBOX11 alternately eleven times over three
                // minutes, every one logged `box: 1, result: "assigned"`.
                //
                // An EMPTY tab stays targeted: that is the genuine "still
                // picking a size" moment (same test as the branch below),
                // where consecutive scans are the operator trying boxes
                // against the goods and must keep resizing, not stack up
                // empty tabs that then block Complete.
                if (prevBox && prevBox.items.length > 0) setReassignTargetId(null);
                playBoxAdd();
                setShowAddBoxModal(false);
                return;
            }

            // Active tab is unassigned → assign it (covers Box 1 on first
            // scan, including the auto-loaded order where handleOrderFound
            // has already stuffed the first item into an unassigned Box 1).
            // Drop into reassign mode ONLY if the tab is still empty — the
            // genuine "operator is still picking a size" moment.
            //
            // "Unassigned" must test boxName as well as boxRef: a CUSTOM
            // box has boxRef null (no dispatch_boxes row to decrement) but
            // boxName set. Testing boxRef alone read a custom-sized tab as
            // unassigned, so pressing "+ Add box" and scanning a real box
            // REPLACED the custom box the operator had already packed items
            // into instead of adding a second one (2026-08-19).
            // applyCustomBox has always tested both; this matches it.
            const active = boxes.find(b => b.id === activeBox);
            if (active && !active.boxRef && !active.boxName
                && (!fromAddBoxModal || active.items.length === 0)) {
                applyBoxAssignment(active.id, box);
                recordBoxScanOutcomeRef.current(
                    { barcode: box.barcode, name: box.name },
                    { boxId: active.id, prevBox: active },
                );
                if (active.items.length === 0) {
                    setReassignTargetId(active.id);
                }
                playBoxAdd();
                setShowAddBoxModal(false);
                return;
            }

            // Otherwise this is a new box — but block if Box 1 still
            // unassigned (a custom Box 1 counts as assigned, hence boxName).
            const box1 = boxes.find(b => b.id === 1);
            if (box1 && !box1.boxRef && !box1.boxName) {
                playError();
                toast.dismiss();
                toast.error(t("assign_box_1_first"));
                return;
            }

            const nextId = boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) + 1 : 1;
            const nextBox: DispatchBox = {
                id: nextId,
                items: [],
                boxRef: box.barcode,
                boxName: box.name,
                boxLengthMm: box.length_mm,
                boxWidthMm: box.width_mm,
                boxHeightMm: box.height_mm,
                boxWeightGrams: box.weight_grams,
            };
            onUpdateBoxes([...boxes, nextBox]);
            logDispatchEvent({ order_pick_type: order.pick_type, event: "box-scan", barcode: box.barcode, order_id: order.order_id, desk_id: deskInfo.deskId, box: nextId, box_name: box.name, result: "assigned-new-tab" });
            recordBoxScanOutcomeRef.current(
                { barcode: box.barcode, name: box.name },
                { boxId: nextId, prevBox: null },
            );
            setActiveBox(nextId);
            // The newly-created tab is now the reassign target. A follow-up
            // box scan resizes it (rather than creating yet another box);
            // the first item scan exits reassign mode and locks the size.
            setReassignTargetId(nextId);
            setNewBoxId(nextId);
            setTimeout(() => setNewBoxId(null), 500);
            playBoxAdd();
            setShowAddBoxModal(false);
        } catch {
            playError();
            toast.dismiss();
            toast.error(t("server_error"));
        }
    }, [boxes, activeBox, reassignTargetId, showAddBoxModal, warehouseBoxes, onUpdateBoxes, t]);

    // Custom box from the add-box modal — no DB row, just user-entered
    // dimensions in cm + grams. The new tab gets boxRef:null so the stock
    // decrement loop skips it (we have nothing to decrement). Naming uses
    // the i18n "Custom" so the tab subtitle still reads "Size: Custom".
    const applyCustomBox = useCallback(() => {
        const lenCm = Number(customBoxForm.length);
        const widCm = Number(customBoxForm.width);
        const hgtCm = Number(customBoxForm.height);
        if (!Number.isFinite(lenCm) || lenCm <= 0
            || !Number.isFinite(widCm) || widCm <= 0
            || !Number.isFinite(hgtCm) || hgtCm <= 0) {
            playError();
            toast.dismiss();
            toast.error(t("box_form_invalid"));
            return;
        }
        const meta = {
            boxRef: null as string | null,
            boxName: t("custom_box_name"),
            boxLengthMm: Math.round(lenCm * 10),
            boxWidthMm: Math.round(widCm * 10),
            boxHeightMm: Math.round(hgtCm * 10),
            // Packaging tare only — see customBoxForm. Not asked for.
            boxWeightGrams: 0,
        };

        // Custom dimensions chosen while a specific box was being
        // recommended is an override like any other — same mandatory
        // reason modal, logged with the sentinel barcode "CUSTOM".
        // (Without this, typing dimensions was a silent way around the
        // override question.)
        const customOutcome = { barcode: "CUSTOM", name: t("custom_box_name") };
        logDispatchEvent({ order_pick_type: order.pick_type, event: "box-custom", order_id: order.order_id, desk_id: deskInfo.deskId, dims_cm: `${lenCm}x${widCm}x${hgtCm}` });

        // This function is ONLY reachable from the "+ Add box" modal, which
        // is an explicit request for another container — so it must never
        // re-target a tab that is already in use.
        //
        // It used to do exactly that, via two branches that belong to the
        // bare-scan flow:
        //   • reassign mode — set by setReassignTargetId on EVERY box
        //     create/assign and cleared only by an item scan, so the tab
        //     just added was almost always still the reassign target. This
        //     is the one that bit: entering dimensions edited the selected
        //     box even when that box was properly assigned.
        //   • "fill the unassigned active tab" — which caught a tab the
        //     operator had already scanned items into.
        //
        // The only tab we may still fill is a bare placeholder (no box, no
        // items): filling that IS adding a box, and creating a sibling
        // beside it would strand an empty tab that blocks Complete
        // (allBoxesHaveItems requires every box to carry something).
        const active = boxes.find(b => b.id === activeBox);
        if (active && !active.boxRef && !active.boxName && active.items.length === 0) {
            const updated = boxes.map(b => b.id === active.id ? { ...b, ...meta } : b);
            onUpdateBoxes(updated);
            recordBoxScanOutcomeRef.current(customOutcome, { boxId: active.id, prevBox: active });
            setReassignTargetId(active.id);
            playBoxAdd();
            setShowAddBoxModal(false);
            return;
        }

        // Box 1 still unassigned → block (mirrors the box-scan rule).
        // Kept deliberately: canComplete only requires SOME box to be
        // assigned, so letting a new box be added beside an unassigned
        // Box 1 would let that box ship as a dimensionless parcel.
        const box1 = boxes.find(b => b.id === 1);
        if (box1 && !box1.boxRef && !box1.boxName) {
            playError();
            toast.dismiss();
            toast.error(t("assign_box_1_first"));
            return;
        }

        const nextId = boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) + 1 : 1;
        const nextBox: DispatchBox = { id: nextId, items: [], ...meta };
        onUpdateBoxes([...boxes, nextBox]);
        recordBoxScanOutcomeRef.current(customOutcome, { boxId: nextId, prevBox: null });
        setActiveBox(nextId);
        setReassignTargetId(nextId);
        setNewBoxId(nextId);
        setTimeout(() => setNewBoxId(null), 500);
        playBoxAdd();
        setShowAddBoxModal(false);
    }, [customBoxForm, boxes, activeBox, onUpdateBoxes, t]);

    const handleScanSubmit = useCallback((scannedValue?: string) => {
        const raw = (scannedValue ?? scan).trim();
        if (!raw) return;
        setScan("");

        // The remove-box confirm and the override-reason modal demand a
        // decision first — swallowing scans stops items landing in a box
        // that's about to be deleted, and stops the mandatory why-did-you-
        // override question being scanned past (the global scan listener
        // stays live behind modals).
        if (confirmRemoveBoxId !== null || overridePrompt !== null || confirmLeave || confirmReset) return;

        // Barcode search open: every scan goes into the search box and only
        // searches, until Close — nothing packs by accident. (A scan typed
        // into the focused search box never reaches here; this catches the
        // ones made after the packer tapped somewhere else.)
        if (searchOpen) {
            setSearchQuery(raw);
            logDispatchEvent({ order_pick_type: order.pick_type, event: "pack-search", barcode: raw, order_id: order.order_id, desk_id: deskInfo.deskId });
            setTimeout(() => {
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
            }, 0);
            return;
        }

        // A scanner whose wedge lags SHIFT delivers the right barcode with
        // its opening letters unshifted ("x0013YYHOZ" for "X0013YYHOZ",
        // "pembOX10" for "PEMBOX10"). Every comparison below is exact, so
        // those scans matched nothing and the packer cleared them by
        // scanning again — 28% of this desk's scans on a bad afternoon.
        // Recover the case against the closed candidate set — the open
        // order's lines plus the warehouse's box list — and ONLY when
        // exactly one candidate matches it; anything ambiguous falls
        // through and is refused exactly as before.
        const candidates = function* (): Generator<string, void, undefined> {
            for (const item of order.items) {
                yield item.sku;
                yield* scannableBarcodes(item);
            }
            for (const b of warehouseBoxes) yield b.barcode;
        };
        const val = recoverScanCaseReported(raw, candidates(), "Dispatch").value;

        // Box scans always route through validate-and-assign before we look
        // for matching order items. Pattern match first; exact match against
        // the warehouse's own box list catches any barcode the pattern
        // misses (exact compare — barcodes are case-significant).
        if (BOX_BARCODE_RE.test(val) || warehouseBoxes.some(b => b.barcode === val)) {
            // Scanning a box instead of the item leaves own packaging.
            if (ownWait) setOwnWait(false);
            handleBoxScan(val);
            return;
        }

        // Find matching item by sku or any of its barcodes (products.barcode
        // + sku_barcodes alternates) that still has units to scan
        const isLine = (item: DispatchOrderItem) => item.sku === val || scannableBarcodes(item).includes(val);

        // "+ › Own packaging" is waiting: this item goes into its own
        // packaging instead of the active box. A scan that matches no line
        // falls through to the usual "not in this order" refusal.
        if (ownWait) {
            const line = order.items.find(i => isLine(i) && ownPackagingUnits(i) > 0) ?? order.items.find(isLine);
            if (line) {
                logDispatchEvent({ order_pick_type: order.pick_type, event: "pack-item-scan", barcode: val, sku: line.sku, order_id: order.order_id, desk_id: deskInfo.deskId, box: activeBox, result: "ok-own-packaging" });
                ownPackagingFromScan(line);
                return;
            }
        }
        const openIndex = order.items.findIndex(item => isLine(item) && resolvedCount(item) < item.quantity);
        // A unit marked missing that turns up while the order is open — a
        // leader hands over a found item (Found items sends them to the
        // desk, JEU718798 2026-10-22), or the picker's missing mark was
        // wrong. The unit in hand beats the mark: the scan takes one
        // missing back and ships it. Not in resolve mode, where
        // missingCount is a refund the leader chose.
        const turnedUpIndex = openIndex < 0 && !resolveMode
            ? order.items.findIndex(item => isLine(item) && (item.missingCount || 0) > 0)
            : -1;
        const matchIndex = openIndex >= 0 ? openIndex : turnedUpIndex;
        const wasMissing = openIndex < 0 && turnedUpIndex >= 0;

        if (matchIndex >= 0) {
            // Never assign a unit to a box that no longer exists — if the
            // active tab was removed (or state restored oddly), fall back
            // to the first box on the bar.
            const targetBox = boxes.some(b => b.id === activeBox) ? activeBox : boxes[0]?.id;
            if (targetBox == null) return;
            if (targetBox !== activeBox) setActiveBox(targetBox);

            const item = order.items[matchIndex];
            const newScanned = item.scannedCount + 1;
            const updated: DispatchOrderItem = {
                ...item,
                scannedCount: newScanned,
                ...(wasMissing ? { missingCount: (item.missingCount || 0) - 1 } : {}),
                assignedBoxes: [...item.assignedBoxes, targetBox],
            };
            updated.scanStatus = deriveScanStatus(updated);

            const updatedItems = [...order.items];
            updatedItems[matchIndex] = updated;
            onUpdateOrder({ ...order, items: updatedItems });

            // Add to current box
            const updatedBoxes = boxes.map(box =>
                box.id === targetBox
                    ? { ...box, items: [...box.items, item.sku] }
                    : box
            );
            onUpdateBoxes(updatedBoxes);

            // Successful item scan ends any in-progress reassign cycle —
            // box scans were re-sizing the targeted tab; now that an item
            // has been scanned, the operator's done picking a size and
            // future box scans add a new tab.
            setReassignTargetId(null);

            playSuccess();
            onItemScanned?.(1);
            logDispatchEvent({ order_pick_type: order.pick_type, event: "pack-item-scan", barcode: val, sku: item.sku, order_id: order.order_id, desk_id: deskInfo.deskId, box: activeBox, result: "ok", ...(wasMissing ? { was_missing: true } : {}) });
            setFlashItemId(item.item_id);
            setTimeout(() => setFlashItemId(null), 300);
        } else {
            // Check if already fully resolved
            const alreadyScanned = order.items.find(
                item => (item.sku === val || scannableBarcodes(item).includes(val)) && resolvedCount(item) >= item.quantity
            );

            if (alreadyScanned) {
                playError();
                setShakeItemId(alreadyScanned.item_id);
                setTimeout(() => setShakeItemId(null), 300);
                toast.dismiss();
                toast.error(t("already_scanned"));
                logDispatchEvent({ order_pick_type: order.pick_type, event: "pack-item-scan", barcode: val, sku: alreadyScanned.sku, order_id: order.order_id, desk_id: deskInfo.deskId, result: "fail-already-scanned" });
            } else {
                playError();
                toast.dismiss();
                logDispatchEvent({ order_pick_type: order.pick_type, event: "pack-item-scan", barcode: val, order_id: order.order_id, desk_id: deskInfo.deskId, result: "fail-not-in-order" });
                // Some codes are not product barcodes at all and never
                // will be — an Amazon Transparency label identifies the
                // UNIT, a QR code on the packaging is a URL, a courier
                // routing string belongs to the box. Saying "not in this
                // order" for those sends the packer looking for a data
                // problem that doesn't exist.
                const kind = classifyUnknownScan(val);
                const kindKey = unknownScanMessageKey(kind);
                // Name the OPEN order and echo the SCANNED value — dispatch
                // restores an unfinished order on re-entry ("must complete
                // it"), and an operator holding a different tote's items
                // otherwise reads this as "scanning is broken" instead of
                // "a previous order is still open". Echoing the value also
                // makes scanner misreads visible at the desk: the operator
                // compares the toast against the sticker (2026-08-06).
                toast.error(
                    kindKey ? t(kindKey) : t("item_not_in_order_number", { value: val, order: order.order_number }),
                    { toastId: UNKNOWN_SCAN_TOAST },
                );
                // …and for anything that COULD be a product, ask the
                // catalogue what it is. A mis-sorted unit then reads as a
                // mis-sort ("Safety Glasses — belongs to order 336829")
                // instead of a dead end, which is the difference between
                // putting it right and flagging the order (2026-08-19).
                if (shouldIdentify(kind)) identifyUnknownScan(val);
            }
        }
    // handleBoxScan MUST be a dependency: it's rebuilt whenever
    // reassignTargetId changes (clicking a box tab to reassign), and clicking
    // the already-active tab doesn't change activeBox — so without this dep
    // handleScanSubmit keeps a stale handleBoxScan that thinks reassign mode
    // is off and adds a new box instead of resizing the clicked one.
    }, [scan, order, boxes, activeBox, confirmRemoveBoxId, overridePrompt, confirmLeave, confirmReset, warehouseBoxes, onUpdateOrder, onUpdateBoxes, handleBoxScan, searchOpen, ownWait, t]);

    // Replace the generic "not in this order" toast in place once the
    // catalogue answers. Deliberately fire-and-forget and non-blocking:
    // the scan has already been rejected and logged, this only improves
    // what the packer is told. Silence on failure — a lookup that errors
    // must never look like a second scan problem.
    const identifyUnknownScan = async (value: string) => {
        try {
            const res = await apiFetch(`/dispatch/identify-barcode`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    barcode: value,
                    warehouse: deskInfo.warehouse,
                    exclude_order_id: order.order_id,
                }),
            });
            if (!res.ok) return;
            const data: IdentifiedBarcode = await res.json();
            const message = describeIdentifiedScan(data, t);
            if (!message) return;
            // Only upgrade OUR toast: if the packer has scanned again in
            // the meantime, theirs is the one on screen and it wins.
            if (!toast.isActive(UNKNOWN_SCAN_TOAST)) return;
            toast.update(UNKNOWN_SCAN_TOAST, { render: message, autoClose: 8000 });
        } catch { /* the generic message stands */ }
    };

    // Each click applies the action to exactly one remaining unit of the item.
    // Users must click the action multiple times to mark multiple units.
    const handleItemAction = (itemId: string, action: "missing" | "damaged" | "wrong_item" | "barcode_damaged") => {
        const actioned = order.items.find(i => i.item_id === itemId);
        if (actioned && resolvedCount(actioned) < actioned.quantity) {
            logDispatchEvent({
                order_pick_type: order.pick_type,
                event: "item-action",
                sku: actioned.sku,
                order_id: order.order_id,
                desk_id: deskInfo.deskId,
                // In flagged-order resolution "missing" is the Refund action.
                result: resolveMode && action === "missing" ? "refund" : action,
            });
        }
        const updatedItems = order.items.map(item => {
            if (item.item_id !== itemId) return item;
            if (resolvedCount(item) >= item.quantity) return item;

            const next: DispatchOrderItem = { ...item };
            if (action === "missing") next.missingCount = item.missingCount + 1;
            else if (action === "damaged") next.damagedCount = item.damagedCount + 1;
            else if (action === "wrong_item") next.wrongItemCount = item.wrongItemCount + 1;
            else {
                // barcode_damaged — the unit IS being shipped, so assign it to the active box
                next.barcodeDamagedCount = item.barcodeDamagedCount + 1;
                next.assignedBoxes = [...item.assignedBoxes, activeBox];
            }
            next.scanStatus = deriveScanStatus(next);
            return next;
        });
        onUpdateOrder({ ...order, items: updatedItems });

        if (action === "barcode_damaged") {
            playSuccess();
            onItemScanned?.(1);
            setFlashItemId(itemId);
            setTimeout(() => setFlashItemId(null), 300);
        } else {
            playError();
        }

        // Close the dropdown only once the row is fully resolved so the user
        // can click again to mark additional units of the same item.
        const justUpdated = updatedItems.find(i => i.item_id === itemId);
        if (justUpdated && resolvedCount(justUpdated) >= justUpdated.quantity) {
            setDropdownItemId(null);
        }
    };

    const toggleDropdown = (itemId: string, anchor?: HTMLElement) => {
        const list = listRef.current;
        if (anchor && list) {
            const a = anchor.getBoundingClientRect();
            const l = list.getBoundingClientRect();
            setDropdownUp(l.height > 0 && a.top - l.top > l.height * 0.5);
        }
        setDropdownItemId(prev => prev === itemId ? null : itemId);
    };

    const handleUnscan = (itemId: string) => {
        const unscanned = order.items.find(i => i.item_id === itemId);
        if (unscanned) {
            logDispatchEvent({ order_pick_type: order.pick_type, event: "item-unscan", sku: unscanned.sku, order_id: order.order_id, desk_id: deskInfo.deskId });
        }
        const updatedItems = order.items.map(item =>
            item.item_id === itemId
                ? {
                    ...item,
                    scanStatus: "pending" as const,
                    scannedCount: 0,
                    missingCount: 0,
                    damagedCount: 0,
                    wrongItemCount: 0,
                    barcodeDamagedCount: 0,
                    assignedBoxes: [],
                }
                : item
        );
        onUpdateOrder({ ...order, items: updatedItems });
        // Reset the auto-book flag so it can fire again once re-scanned
        hasAutoBooked.current = false;
        preloadResultRef.current = null;
        preloadingRef.current = false;
    };

    // Units of a line that could still go into its own packaging: the ones
    // not scanned yet plus every scanned unit not already in own packaging,
    // whether it sits in a tab still waiting for its box or in a real box.
    // "This ships as it is" is usually decided with the parcel in hand —
    // often after it went into a box — so the button stays on scanned lines
    // and the unit comes back out of that box (2026-10-26).
    const ownPackagingUnits = (item: DispatchOrderItem) =>
        ownPackagingUnitsFor(item, boxes, item.quantity - resolvedCount(item));
    // Open the own-packaging dialog, prefilled from the last confirmed
    // measurement of this SKU (or of another SKU with the same ASIN), else
    // the product's own size and weight, else blank. Everything stays
    // editable; Confirm saves what was used for next time.
    const openOwnPkgPrompt = (item: DispatchOrderItem, prefer: "boxed" | "fresh") => {
        const defaults = ownPackagingDefaults(item, ownPkgMemory.specs[item.sku]);
        setOwnPkgForm({
            length: defaults.length,
            width: defaults.width,
            height: defaults.height,
            qty: "1",
            weight: kgField(defaults.unitWeightG),
            weightEdited: false,
        });
        setOwnPkgPrompt({ itemId: item.item_id, prefer, defaults });
    };

    // "Ship this item in its own packaging": ONE container, sized as given,
    // holding `units` units of the line. planOwnPackaging (ownPackaging.ts)
    // decides which units: scanned ones come out of the box they were in,
    // unscanned ones count as scanned. A box left empty comes off the bar.
    // `weightG` (optional) is the container as weighed — it becomes the
    // parcel's contents weight while the box holds those units. Box shape:
    // boxRef:null, boxName=Own packaging, tare 0.
    // `remember` (default true): the packer confirmed these numbers in the
    // dialog, so they are saved for the next pack of this SKU / ASIN.
    // POST /dispatch/own-packaging also flags the product ship_in_packaging
    // so the button is highlighted (hint only — no auto-fill) next time.
    const applyOwnPackaging = useCallback((
        itemId: string,
        opts: { lCm: number; wCm: number; hCm: number; weightG?: number | null; units: number; prefer: "boxed" | "fresh"; remember?: boolean },
    ) => {
        const item = order.items.find(i => i.item_id === itemId);
        if (!item) return;

        const { lCm, wCm, hCm } = opts;
        if (!lCm || !wCm || !hCm) {
            playError();
            return;
        }

        const plan = planOwnPackaging(item, boxes, {
            units: opts.units,
            unscanned: item.quantity - resolvedCount(item),
            prefer: opts.prefer,
        });
        if (plan.total <= 0) return;

        const lMm = Math.round(lCm * 10);
        const wMm = Math.round(wCm * 10);
        const hMm = Math.round(hCm * 10);
        const ownName = t("ship_in_packaging_box_name");
        // The weighing describes the container the packer asked for; if
        // fewer units were available than they typed, it describes nothing
        // we built, so the catalogue weight stands.
        const weighedG = opts.weightG != null && opts.weightG > 0 && plan.total === Math.floor(opts.units)
            ? Math.round(opts.weightG)
            : null;
        const weighed = weighedG != null ? { measuredWeightGrams: weighedG, measuredUnits: plan.total } : {};

        // box.items carries one SKU entry per unit, so pull exactly as many
        // as leave each box.
        const workingBoxes: DispatchBox[] = boxes.map(b => {
            const source = plan.fromBoxes.find(s => s.boxId === b.id);
            if (!source) return b;
            let pull = source.units;
            return {
                ...b,
                items: b.items.filter(sku => {
                    if (sku === item.sku && pull > 0) { pull -= 1; return false; }
                    return true;
                }),
            };
        });
        // ONE container holding every unit — one SKU entry per unit, so the
        // courier step still sums the right weight and unit count. A tab
        // still waiting for its box that is now empty becomes the container
        // (Box 1 usually, keeping the bar tidy); otherwise a new tab.
        const skuEntries = Array.from({ length: plan.total }, () => item.sku);
        const placeholderIdx = workingBoxes.findIndex(b =>
            !b.boxRef && !b.boxName && !b.boxLengthMm && b.items.length === 0,
        );
        let targetBoxId: number;
        if (placeholderIdx >= 0) {
            targetBoxId = workingBoxes[placeholderIdx].id;
            workingBoxes[placeholderIdx] = {
                ...workingBoxes[placeholderIdx],
                boxName: ownName,
                boxLengthMm: lMm,
                boxWidthMm: wMm,
                boxHeightMm: hMm,
                boxWeightGrams: 0,
                items: skuEntries,
                ...weighed,
            };
        } else {
            targetBoxId = (boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) : 0) + 1;
            workingBoxes.push({
                id: targetBoxId,
                items: skuEntries,
                boxRef: null,
                boxName: ownName,
                boxLengthMm: lMm,
                boxWidthMm: wMm,
                boxHeightMm: hMm,
                boxWeightGrams: 0,
                ...weighed,
            });
        }
        // assignedBoxes carries one entry per unit, so the same id repeats.
        const newAssignments = Array.from({ length: plan.total }, () => targetBoxId);
        const taken = new Set(plan.takenIndexes);

        const updatedItems = order.items.map(i => {
            if (i.item_id !== itemId) return i;
            const next: DispatchOrderItem = {
                ...i,
                ship_in_packaging: true,
                scannedCount: i.scannedCount + plan.fresh,
                assignedBoxes: [...i.assignedBoxes.filter((_, idx) => !taken.has(idx)), ...newAssignments],
            };
            next.scanStatus = deriveScanStatus(next);
            return next;
        });

        // A box left holding nothing would sit on the bar and block Complete
        // (every box must carry an item) — and a real box we no longer use
        // must not be booked or taken from stock. So drop every empty box
        // nothing points at, except a real box the packer has scanned but
        // not filled yet and that we did not just empty.
        const referenced = new Set(updatedItems.flatMap(i => i.assignedBoxes));
        const emptied = new Set(plan.fromBoxes.map(s => s.boxId));
        const prunedBoxes = workingBoxes.filter(b =>
            b.items.length > 0 || referenced.has(b.id) || (isBoxAssigned(b) && !emptied.has(b.id)),
        );
        const finalBoxes = prunedBoxes.length > 0 ? prunedBoxes : workingBoxes;
        if (!finalBoxes.some(b => b.id === activeBox)) {
            setActiveBox((finalBoxes.find(b => !isOwnPackagingBox(b)) ?? finalBoxes[0]).id);
        }

        onUpdateBoxes(finalBoxes);
        onUpdateOrder({ ...order, items: updatedItems });

        // Units left a real box: the parcels changed, so a label booked for
        // the old set must be re-booked — same as removing a box.
        const boxedSources = plan.fromBoxes.filter(s => s.boxed);
        if (boxedSources.length > 0) {
            hasAutoBooked.current = false;
            preloadResultRef.current = null;
            preloadingRef.current = false;
        }

        playBoxAdd();
        playSuccess();
        // Only NEW scans count towards the session stats — re-homing a
        // unit that was already scanned isn't another scan.
        if (plan.fresh > 0) onItemScanned?.(plan.fresh);
        setFlashItemId(itemId);
        setTimeout(() => setFlashItemId(null), 300);
        setDropdownItemId(null);
        logDispatchEvent({
            order_pick_type: order.pick_type,
            event: "own-packaging",
            order_id: order.order_id,
            desk_id: deskInfo.deskId,
            sku: item.sku,
            box: targetBoxId,
            units: plan.total,
            unscanned_units: plan.fresh,
            from_boxes: plan.fromBoxes.map(s => `${s.boxId}:${s.units}`).join(","),
            via: opts.prefer === "fresh" ? "box-bar-scan" : "line-button",
        });

        toast.dismiss();
        toast.success(
            boxedSources.length > 0
                ? t("ship_in_packaging_moved", {
                    sku: item.sku,
                    from: boxedSources.map(s => s.boxId).join(", "),
                    to: String(targetBoxId),
                })
                : t("ship_in_packaging_enabled", { sku: item.sku }),
            { toastId: `sip-${item.sku}` },
        );

        // Persist for future orders — fire-and-forget so packing never waits
        // on the round trip. POST /dispatch/own-packaging is on the bench's
        // own key: the /products/bulk call this replaced is admin-only and
        // refused 769 packer saves in the 30 days to 2026-10-28.
        //
        // The BE always flags the product ship_in_packaging (the button hint
        // next time; it never pre-boxes the item). When the numbers were
        // confirmed in the dialog they ride along: a SINGLE-unit measurement
        // is remembered under the SKU and its ASIN, and fills the product's
        // dims / weight only where it has none. Several units strapped
        // together are a one-off parcel, not the unit's size, so the BE
        // stores nothing for them.
        const remember = opts.remember !== false;
        apiFetch(`/dispatch/own-packaging`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                sku: item.sku,
                units: plan.total,
                order_id: order.order_id,
                ...(remember ? { length_cm: lCm, width_cm: wCm, height_cm: hCm, weight_grams: weighedG } : {}),
            }),
        }).catch(() => { /* memory only, no UX surface */ });
        // The next dialog on this order — this SKU, or another line of the
        // same ASIN — opens with what was just confirmed.
        if (remember && plan.total === 1) {
            setOwnPkgMemory(prev => {
                const asin = prev.asins[item.sku] ?? null;
                const previous = prev.specs[item.sku];
                const spec: OwnPackagingSpec = {
                    length_cm: lCm,
                    width_cm: wCm,
                    height_cm: hCm,
                    // A blank weight keeps the last one weighed, as on the BE.
                    weight_grams: weighedG ?? previous?.weight_grams ?? null,
                    matched_by: "sku",
                    asin,
                    source_sku: item.sku,
                    updated_at: new Date().toISOString(),
                    updated_by_name: getCurrentUser().name || null,
                };
                const specs = { ...prev.specs, [item.sku]: spec };
                if (asin) {
                    for (const [sku, a] of Object.entries(prev.asins)) {
                        if (a === asin && sku !== item.sku) specs[sku] = { ...spec, matched_by: "asin" };
                    }
                }
                return { ...prev, specs };
            });
        }
    }, [order, boxes, activeBox, onUpdateBoxes, onUpdateOrder, onItemScanned, t]); // eslint-disable-line react-hooks/exhaustive-deps

    // The unit scanned after "+ › Own packaging". With a size on file — the
    // last confirmed own-packaging measurement, else the product's own — it
    // goes straight into its own packaging; without one the dialog asks for
    // a measurement first. Either way the next scan goes back to the box
    // being filled. Nothing new was confirmed on the fast path, so it only
    // flags the product (remember: false) and never overwrites the memory.
    const ownPackagingFromScan = (item: DispatchOrderItem) => {
        if (ownPackagingUnits(item) <= 0) {
            playError();
            toast.dismiss();
            toast.error(t("dispatch_own_pkg_already", { sku: item.sku }));
            return;
        }
        setOwnWait(false);
        const defaults = ownPackagingDefaults(item, ownPkgMemory.specs[item.sku]);
        if (defaults.length && defaults.width && defaults.height) {
            applyOwnPackaging(item.item_id, {
                lCm: Number(defaults.length),
                wCm: Number(defaults.width),
                hCm: Number(defaults.height),
                // A saved bench weighing still applies; a catalogue weight
                // is what the booking would use anyway.
                weightG: defaults.spec ? defaults.spec.weight_grams : null,
                units: 1,
                prefer: "fresh",
                remember: false,
            });
            return;
        }
        openOwnPkgPrompt(item, "fresh");
    };

    // "+ › New box": a tab waiting for SCAN BOX becomes the active one, so
    // the next box scan sizes it and the items scanned after go in it.
    // Same Box 1 rule as a box scan: Box 1 needs its box first.
    const openNewBoxTab = () => {
        setBoxMenu(null);
        setOwnWait(false);
        const bare = boxes.find(b => !b.boxRef && !b.boxName && b.items.length === 0
            && !order.items.some(i => i.assignedBoxes.includes(b.id)));
        if (bare) {
            setActiveBox(bare.id);
            setReassignTargetId(null);
            return;
        }
        const box1 = boxes.find(b => b.id === 1);
        if (box1 && !box1.boxRef && !box1.boxName) {
            playError();
            toast.dismiss();
            toast.error(t("assign_box_1_first"));
            return;
        }
        const newId = boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) + 1 : 1;
        onUpdateBoxes([...boxes, { id: newId, items: [] }]);
        setActiveBox(newId);
        // A reassign target left over from the last box would make the next
        // box scan resize THAT tab instead of filling this one.
        setReassignTargetId(null);
        playBoxAdd();
        setNewBoxId(newId);
        setTimeout(() => setNewBoxId(null), 500);
    };

    const toggleBoxMenu = (anchor: HTMLElement) => {
        if (boxMenu) { setBoxMenu(null); return; }
        const rect = anchor.getBoundingClientRect();
        setBoxMenu({ left: rect.left, bottom: window.innerHeight - rect.top + 10 });
    };
    // The menu closes on any tap outside it (the + toggles it).
    useEffect(() => {
        if (!boxMenu) return;
        const close = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest(".dispatch-box-menu, .dispatch-box-tab--add")) return;
            setBoxMenu(null);
        };
        const timer = setTimeout(() => window.addEventListener("click", close), 0);
        return () => { clearTimeout(timer); window.removeEventListener("click", close); };
    }, [boxMenu]);

    // Search: open focuses the box; Close clears it and scans pack again.
    const openSearch = () => {
        setSearchOpen(true);
        setSearchQuery("");
        setTimeout(() => searchInputRef.current?.focus(), 0);
    };
    const closeSearch = () => {
        setSearchOpen(false);
        setSearchQuery("");
        searchInputRef.current?.blur();
    };
    // A new order starts with the search, the + menu and own packaging shut.
    useEffect(() => {
        setSearchOpen(false);
        setSearchQuery("");
        setOwnWait(false);
        setBoxMenu(null);
    }, [order.order_id]);

    // Close dropdown on any outside click
    useEffect(() => {
        if (!dropdownItemId) return;
        const close = () => setDropdownItemId(null);
        // Delay listener so the opening click doesn't immediately close it
        const timer = setTimeout(() => window.addEventListener("click", close), 0);
        return () => { clearTimeout(timer); window.removeEventListener("click", close); };
    }, [dropdownItemId]);

    const addBox = () => {
        const newId = boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) + 1 : 1;
        onUpdateBoxes([...boxes, { id: newId, items: [] }]);
        setActiveBox(newId);
        playBoxAdd();
        setNewBoxId(newId);
        setTimeout(() => setNewBoxId(null), 500);
    };

    // Safety net: whatever path mutates the box list (removal, session
    // restore, ship-in-packaging rebuild), the active tab and any reassign
    // target must always point at a box that still exists — otherwise item
    // scans would keep assigning units to a ghost box with no visible tab.
    useEffect(() => {
        if (boxes.length === 0) return;
        if (!boxes.some(b => b.id === activeBox)) setActiveBox(boxes[0].id);
        if (reassignTargetId !== null && !boxes.some(b => b.id === reassignTargetId)) {
            setReassignTargetId(null);
        }
    }, [boxes, activeBox, reassignTargetId]);

    const removeBox = (boxId: number) => {
        // If box has items, ask for confirmation first
        const hasItems = order.items.some(i => i.assignedBoxes.includes(boxId));
        if (hasItems) {
            setConfirmRemoveBoxId(boxId);
            return;
        }
        doRemoveBox(boxId);
    };

    const doRemoveBox = (boxId: number) => {
        logDispatchEvent({ order_pick_type: order.pick_type, event: "box-remove", order_id: order.order_id, desk_id: deskInfo.deskId, box: boxId });
        const idx = boxes.findIndex(b => b.id === boxId);
        // Removing the LAST box resets the order to a fresh unassigned
        // Box 1 rather than leaving zero tabs — items scanned with no box
        // chosen conceptually live in Box 1, and with no box assigned the
        // recommendation pivots back to the whole-order suggestion (the
        // reopen → unscan → repack flow relies on this).
        const updated = boxes.length <= 1
            ? [{ id: 1, items: [] } as DispatchBox]
            : boxes.filter(b => b.id !== boxId);
        onUpdateBoxes(updated);

        // Remove shipped units assigned to this box. We can't tell a scanned
        // unit from a barcode-damaged unit within a box, so we peel them off
        // the scanned count first, then the barcode-damaged count.
        const updatedItems = order.items.map(item => {
            if (!item.assignedBoxes.includes(boxId)) return item;
            const remaining = item.assignedBoxes.filter(b => b !== boxId);
            let removed = item.assignedBoxes.length - remaining.length;
            let scanned = item.scannedCount;
            let barcodeDamaged = item.barcodeDamagedCount;
            const takeScanned = Math.min(scanned, removed);
            scanned -= takeScanned;
            removed -= takeScanned;
            barcodeDamaged = Math.max(0, barcodeDamaged - removed);
            const next: DispatchOrderItem = {
                ...item,
                assignedBoxes: remaining,
                scannedCount: scanned,
                barcodeDamagedCount: barcodeDamaged,
            };
            next.scanStatus = deriveScanStatus(next);
            return next;
        });
        onUpdateOrder({ ...order, items: updatedItems });
        hasAutoBooked.current = false;
        preloadResultRef.current = null;
        preloadingRef.current = false;
        setConfirmRemoveBoxId(null);

        if (activeBox === boxId) {
            const prevIdx = Math.min(Math.max(0, idx - 1), updated.length - 1);
            setActiveBox(updated[prevIdx].id);
        }
    };

    // Reset order (plan 7a): one empty Box 1, every line back to pending —
    // units pre-marked missing upstream stay marked. Re-arms auto-book the
    // same way handleUnscan / doRemoveBox do.
    const doResetOrder = () => {
        const next = resetOrderState(order, boxes);
        logDispatchEvent({
            order_pick_type: order.pick_type,
            event: "order-reset",
            order_id: order.order_id,
            desk_id: deskInfo.deskId,
            boxes: next.cleared.boxes,
            units: next.cleared.units,
            marked: next.cleared.marked,
        });
        onOrderReset?.();
        onUpdateBoxes(next.boxes);
        onUpdateOrder(next.order);
        setActiveBox(next.activeBox);
        setReassignTargetId(null);
        hasAutoBooked.current = false;
        preloadResultRef.current = null;
        preloadingRef.current = false;
        setConfirmReset(false);
    };

    const handleCompleteClick = () => {
        bookCourier();
    };

    const getRowClass = (item: DispatchOrderItem, done = false) => {
        const classes = ["dispatch-item-row"];
        // Finished and settled at the bottom: drawn as one compact line.
        if (done) classes.push("dispatch-item-row--done");
        if (item.scanStatus === "scanned") classes.push("dispatch-item-row--scanned");
        if (item.scanStatus === "missing") classes.push("dispatch-item-row--missing");
        if (item.scanStatus === "damaged") classes.push("dispatch-item-row--damaged");
        // Any missing units — pre-marked upstream (picking / sorting flag
        // flows) or flagged at the desk — turn the row amber regardless of
        // the derived status, so a partially-shipped or multi-qty row can
        // never read as cleanly scanned while units are known missing.
        if ((item.missingCount || 0) > 0) classes.push("dispatch-item-row--flagged");
        if (item.flagged_pick) classes.push("dispatch-item-row--flagged-pick");
        if (flashItemId === item.item_id) classes.push("dispatch-item-row--flash-green");
        if (shakeItemId === item.item_id) classes.push("dispatch-item-row--shake");
        return classes.join(" ");
    };

    const getStatusIcon = (item: DispatchOrderItem) => {
        if (item.scanStatus === "scanned") return <Check size={22} color="var(--color-success)" />;
        if (item.scanStatus === "missing") return <AlertTriangle size={22} color="var(--color-warning)" />;
        if (item.scanStatus === "damaged") return <X size={22} color="var(--color-error)" />;
        return null;
    };

    const flagReason = resolveMode?.flagInfo?.reason;
    const flagReasonBy = resolveMode?.flagInfo?.by;

    // Layout (2026-10-23 bench redesign): order bar, optional notice
    // strips, the item list (the only part that scrolls) and the box bar,
    // stacked in one full-height column. Sizes follow the screen's HEIGHT
    // as well as its width (dispatch-bench.css), so the 1366×768 Polish
    // bench panels show five-plus lines instead of under two.
    return (
        <div className="dispatch-page dispatch-bench dispatch-pack">
            <DispatchOrderBar
                order={order}
                done={totalResolved}
                total={totalRequired}
                weightKg={totalWeight / 1000}
                actions={(
                    <>
                        <button
                            type="button"
                            className={`dispatch-bar-icon-btn dispatch-search-btn${searchOpen ? " dispatch-search-btn--on" : ""}`}
                            onClick={() => (searchOpen ? closeSearch() : openSearch())}
                            aria-label={t("dispatch_search_open")}
                            aria-pressed={searchOpen}
                            title={t("dispatch_search_open")}
                        >
                            <Search size={20} aria-hidden="true" />
                        </button>
                        {!resolveMode && !readOnly && hasDeskProgress(order, boxes) && (
                            <button
                                type="button"
                                className="dispatch-bar-btn dispatch-reset-order-btn"
                                onClick={() => setConfirmReset(true)}
                                aria-label={t("reset_order")}
                            >
                                <RotateCcw size={16} aria-hidden="true" />
                                {t("dispatch_reset_short")}
                            </button>
                        )}
                        {onFlagOrder && (
                            <button
                                type="button"
                                className="dispatch-bar-btn dispatch-flag-btn"
                                onClick={onFlagOrder}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                aria-label={t("flag_order")}
                                title={readOnly ? t("read_only_mode") : t("flag_order")}
                            >
                                <Flag size={16} aria-hidden="true" />
                                {t("dispatch_flag_short")}
                            </button>
                        )}
                        {/* Way OUT of an open order — without this, Complete
                            and Flag were the only exits and a restored/
                            mis-opened order trapped the operator (2026-08-06).
                            Backing out with progress asks first; the parent's
                            goBack clears the persisted order so re-entry
                            starts at the tote scan, and the order (still
                            'dispatch' BE-side) reopens fresh any time. */}
                        <button
                            type="button"
                            className="dispatch-bar-icon-btn"
                            onClick={() => {
                                const hasProgress = order.items.some(i => resolvedCount(i) > 0)
                                    || boxes.some(isBoxAssigned);
                                if (hasProgress) setConfirmLeave(true);
                                else goBack();
                            }}
                            aria-label={t("leave_order")}
                        >
                            <ArrowLeft size={20} />
                        </button>
                    </>
                )}
            />

            {/* Barcode search strip, under the order bar while searching.
                Typing or scanning into the box (or anywhere, until Close)
                only searches; Enter keeps the result and selects it, so the
                next scan replaces it. Esc closes, like Close. */}
            {searchOpen && (
                <div role="search" className={`dispatch-search-strip${searchTerm && searchHits.size === 0 ? " dispatch-search-strip--none" : ""}`}>
                    <label className="dispatch-search-field">
                        <Search size={18} aria-hidden="true" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="dispatch-search-input"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter") { e.preventDefault(); e.currentTarget.select(); }
                                if (e.key === "Escape") { e.preventDefault(); closeSearch(); }
                            }}
                            onFocus={e => e.currentTarget.select()}
                            aria-label={t("dispatch_search_label")}
                            placeholder={t("dispatch_search_placeholder")}
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </label>
                    <span className="dispatch-search-hint">
                        {searchTerm && searchHits.size === 0 ? t("dispatch_search_none_hint") : t("dispatch_search_hint")}
                    </span>
                    <span className="dispatch-search-spacer" />
                    {searchTerm && (
                        <span role="status" className="dispatch-search-result">
                            {searchHits.size === 0
                                ? t("dispatch_search_none")
                                : t("dispatch_search_count", { count: String(searchHits.size) })}
                        </span>
                    )}
                    <button type="button" className="dispatch-bar-btn dispatch-search-close" onClick={closeSearch}>
                        <X size={18} aria-hidden="true" />
                        {t("close")}
                    </button>
                </div>
            )}

            {/* A two-cubby Putwall order arrived in two totes — the packer
                needs both at the desk before this order is complete. */}
            {unitTotes && unitTotes.length > 1 && (
                <div className="dispatch-notice dispatch-notice--totes dispatch-unit-totes" role="status">
                    <Package size={16} aria-hidden="true" />
                    {t("dispatch_order_in_totes", {
                        count: String(unitTotes.length),
                        totes: unitTotes.map(x => `${x.tote_id} (${x.items})`).join(", "),
                    })}
                </div>
            )}

            {/* Resolve mode: why this order was flagged, and by whom. */}
            {flagReason && (
                <div className="dispatch-notice dispatch-notice--flag">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <span>
                        <strong>{flagReason}</strong>
                        {flagReasonBy && (
                            <span className="dispatch-flagged-reason-by"> &mdash; {flagReasonBy}</span>
                        )}
                    </span>
                </div>
            )}

            {/* Items list — unresolved first, resolved at the bottom (a line
                finished a moment ago holds its place while it flashes; while
                searching, matching lines come first). Each row's controls
                sit in fixed columns, right to left: undo, item issue (or the
                done tick), own packaging, quantity, then any warning message
                — so every button is in the same place on every line. */}
            <div className="dispatch-pack-list-wrap">
                <div
                    className={`dispatch-items-list${showImages ? "" : " dispatch-items-list--no-images"}`}
                    ref={listRef}
                    onScroll={measureMoreBelow}
                    onTransitionEnd={measureMoreBelow}
                >
                    {listItems.map(item => {
                        const resolved = isItemResolved(item);
                        const done = resolved && !heldIds.has(item.item_id);
                        const hit = searchHits.get(item.item_id);
                        return (
                            <div key={item.item_id} className={`${getRowClass(item, done)}${hit ? " dispatch-item-row--search-hit" : ""}`}>
                                {showImages && (
                                    item.image_url ? (
                                        <img src={item.image_url} alt="" className="dispatch-item-thumb" />
                                    ) : (
                                        <div className="dispatch-item-thumb dispatch-item-thumb--empty" />
                                    )
                                )}

                                <div className="dispatch-item-info">
                                    <p className="dispatch-item-title">{item.title || item.sku}</p>
                                    <p className="dispatch-item-meta">
                                        <span className="dispatch-item-sku">
                                            {hit?.field === "sku" ? <SearchMarked hit={hit} /> : item.sku}
                                        </span>
                                        {/* A search match shows the code it matched, marked. */}
                                        {hit?.field === "barcode" ? (
                                            <span className="dispatch-item-barcodes"><SearchMarked hit={hit} /></span>
                                        ) : (
                                            <ItemBarcodes item={item} />
                                        )}
                                        {item.assignedBoxes.length > 0 && (
                                            <span className="dispatch-item-box-tag">
                                                {[...new Set(item.assignedBoxes)].map(b => `${t("box_label")} ${b}`).join(", ")}
                                            </span>
                                        )}
                                    </p>
                                </div>

                                {/* Warning messages. Explicit missing chip: the
                                    resolved count treats missing units as
                                    "accounted for", which used to read as
                                    scanned — the chip names them. Flagged
                                    Picks: the unit is in stock and on its way
                                    (or on the staging shelf) — never a
                                    missing count. */}
                                {((item.missingCount || 0) > 0 || item.flagged_pick) && (
                                    <div className="dispatch-item-flags">
                                        {(item.missingCount || 0) > 0 && (
                                            <span className="dispatch-item-missing-chip">
                                                {resolveMode ? <CircleDollarSign size={13} /> : <AlertTriangle size={13} />}
                                                {t(resolveMode ? "dispatch_item_refund_chip" : "dispatch_item_missing_chip", { count: String(item.missingCount) })}
                                            </span>
                                        )}
                                        {item.flagged_pick && (
                                            <span className={`dispatch-item-fp-chip${item.flagged_pick.status === "staged" ? " dispatch-item-fp-chip--staged" : ""}`} data-testid="dispatch-item-fp-chip">
                                                {item.flagged_pick.status === "staged"
                                                    ? t("fp_item_chip_shelf", { location: stagingShelfLabel(item.flagged_pick) })
                                                    : t("fp_item_chip_awaiting")}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="dispatch-item-controls">
                                    <div className="dispatch-item-slot dispatch-item-slot--qty">
                                        {item.quantity > 1 && (
                                            <span className={`dispatch-item-qty ${resolved ? "dispatch-item-qty--complete" : ""}`}>
                                                {resolvedCount(item)}/{item.quantity}
                                            </span>
                                        )}
                                    </div>

                                    <div className="dispatch-item-slot dispatch-item-slot--pkg">
                                        {/* Stays on scanned lines too, until every
                                            unit is in its own packaging — the "this
                                            ships as it is" call is usually made with
                                            the parcel in hand, often after it went
                                            into a box (it comes back out). */}
                                        {ownPackagingUnits(item) > 0 && (
                                            <button
                                                type="button"
                                                // --hinted: the product is flagged
                                                // ship_in_packaging (a previous pack of
                                                // this SKU went out in its own box), so
                                                // the button is drawn filled as a nudge.
                                                // It's a hint, never a decision — see the
                                                // note above the item list.
                                                className={`dispatch-item-action-btn dispatch-item-action-btn--own-pkg${item.ship_in_packaging ? " dispatch-item-action-btn--own-pkg-hinted" : ""}`}
                                                // Always live now. Missing dimensions used
                                                // to disable this, which dead-ended the
                                                // operator holding the item — the modal
                                                // lets them measure it instead.
                                                onClick={() => {
                                                    setOwnWait(false);
                                                    openOwnPkgPrompt(item, "boxed");
                                                }}
                                                title={t(item.ship_in_packaging ? "ship_in_own_packaging_hinted" : "ship_in_own_packaging_action")}
                                                aria-label={t("ship_in_own_packaging_action")}
                                            >
                                                <PackageOpen size={18} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="dispatch-item-slot dispatch-item-slot--issue">
                                        {!resolved ? (
                                            <div className="dispatch-item-actions">
                                                <button
                                                    type="button"
                                                    className="dispatch-item-action-btn"
                                                    onClick={e => toggleDropdown(item.item_id, e.currentTarget)}
                                                    aria-haspopup="true"
                                                    aria-expanded={dropdownItemId === item.item_id}
                                                >
                                                    <AlertTriangle size={18} />
                                                </button>
                                                {dropdownItemId === item.item_id && (
                                                    <div className={`dispatch-item-dropdown${dropdownUp ? " dispatch-item-dropdown--up" : ""}`}>
                                                        {/* Resolve mode: the leader's decision for an
                                                            unshippable unit is a REFUND (stored as
                                                            missingCount here, converted on persist),
                                                            not a fresh missing/damaged flag. */}
                                                        {resolveMode ? (
                                                            <button
                                                                className="dispatch-item-dropdown-option dispatch-item-dropdown-option--danger"
                                                                onClick={() => handleItemAction(item.item_id, "missing")}
                                                                disabled={resolveMode.canRefund === false}
                                                                aria-disabled={resolveMode.canRefund === false}
                                                                title={resolveMode.canRefund === false ? t("resolve_refund_no_grant") : undefined}
                                                            >
                                                                <CircleDollarSign size={16} />
                                                                {t("refund")}
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    className="dispatch-item-dropdown-option"
                                                                    onClick={() => handleItemAction(item.item_id, "missing")}
                                                                >
                                                                    <PackageMinus size={16} />
                                                                    {t("item_missing")}
                                                                </button>
                                                                <button
                                                                    className="dispatch-item-dropdown-option dispatch-item-dropdown-option--danger"
                                                                    onClick={() => handleItemAction(item.item_id, "damaged")}
                                                                >
                                                                    <AlertTriangle size={16} />
                                                                    {t("item_damaged")}
                                                                </button>
                                                                <button
                                                                    className="dispatch-item-dropdown-option dispatch-item-dropdown-option--danger"
                                                                    onClick={() => handleItemAction(item.item_id, "wrong_item")}
                                                                >
                                                                    <RefreshCw size={16} />
                                                                    {t("item_wrong")}
                                                                </button>
                                                            </>
                                                        )}
                                                        <button
                                                            className="dispatch-item-dropdown-option"
                                                            onClick={() => handleItemAction(item.item_id, "barcode_damaged")}
                                                        >
                                                            <ScanLine size={16} />
                                                            {t("item_barcode_damaged")}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="dispatch-item-status">{getStatusIcon(item)}</span>
                                        )}
                                    </div>

                                    <div className="dispatch-item-slot dispatch-item-slot--undo">
                                        {resolvedCount(item) > 0 && (
                                            <button
                                                type="button"
                                                className="dispatch-item-action-btn dispatch-item-action-btn--unscan"
                                                onClick={() => handleUnscan(item.item_id)}
                                                title={t("unscan")}
                                                aria-label={t("unscan")}
                                            >
                                                <Undo2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {moreBelow > 0 && (
                    <button type="button" className="dispatch-more-below" onClick={scrollListDown}>
                        <ChevronsDown size={16} aria-hidden="true" />
                        {t("dispatch_more_below", { count: String(moreBelow) })}
                    </button>
                )}
            </div>

            {/* Box bar: the next-box suggestion, the box tabs (a tab still
                waiting for its box reads SCAN BOX — this replaced the red
                prompt that floated over the list), the session counts and
                Complete. Nothing on the pack screen floats over the list
                any more. */}
            <div className="dispatch-box-bar">
                {liveBookingState && boxes.length !== liveBookingState.pieces && (
                    <p className="dispatch-box-bar-live-warning" role="status">
                        <AlertTriangle size={14} />
                        <span>{t("carrier_live_booking_box_warning", {
                            booked: String(liveBookingState.pieces),
                            packed: String(boxes.length),
                        })}</span>
                    </p>
                )}

                {/* Box recommendation — display-only suggestion. Stays up
                    until a box has actually been assigned, even once every
                    item is scanned — that's exactly when the operator walks
                    off to grab the box. It replaced the full-screen SCAN BOX
                    veil (2026-10-23), which covered a single-item order the
                    moment it opened. Hidden when there's nothing to surface
                    or the warehouse has the feature toggled off. The
                    "skipped" chip shows a missing-dims warning even with no
                    box suggestion. */}
                {boxRecommendationEnabled && (!allResolved || !hasScannedBox) && (
                    recommendation.boxes.length > 0 ||
                    !recommendation.fits ||
                    recommendation.skippedSkus.length > 0
                ) && (
                    <div className="dispatch-recommend-banner">
                        <Lightbulb size={22} className="dispatch-recommend-banner-icon" aria-hidden="true" />
                        {!recommendation.fits && (
                            <span className="dispatch-recommend-banner-warning">
                                {t("box_recommendation_no_fit")}
                            </span>
                        )}
                        {recommendation.fits && recommendation.boxes.length > 0 && (
                            <span className="dispatch-recommend-banner-text">
                                <span className="dispatch-recommend-banner-label">{t("next_box")}</span>
                                {recommendation.boxes.map(b => (
                                    <span key={b.barcode} className="dispatch-recommend-banner-pill">{b.name}</span>
                                ))}
                            </span>
                        )}
                        {/* Fragile: the order can't go in a plastic mailbag.
                            Treats every item as extra-protection, so the
                            suggestion re-computes without the containers this
                            warehouse excludes. Tap again to undo. Sits before
                            the hint chips so it is never the one clipped. */}
                        <button
                            type="button"
                            className={`dispatch-recommend-banner-chip dispatch-recommend-banner-chip--fragile${fragileOverride ? " dispatch-recommend-banner-chip--fragile-on" : ""}`}
                            onClick={() => setFragileOverride(v => !v)}
                            aria-pressed={fragileOverride}
                            title={fragileOverride ? t("box_recommendation_fragile_clear") : undefined}
                        >
                            <ShieldAlert size={14} />
                            {fragileOverride ? t("box_recommendation_fragile_on") : t("box_recommendation_fragile")}
                        </button>
                        {/* The suggestion only works if the flagged items go
                            in DIAGONALLY — without this hint the operator
                            tries them straight, concludes the tool is wrong,
                            and grabs a bigger box. */}
                        {recommendation.fits && recommendation.boxes.length > 0 && recommendation.diagonalSkus.length > 0 && (
                            <span
                                className="dispatch-recommend-banner-chip dispatch-recommend-banner-chip--diagonal"
                                title={t("box_recommendation_diagonal", { skus: recommendation.diagonalSkus.join(", ") })}
                            >
                                <MoveDiagonal size={14} />
                                {t("box_recommendation_diagonal", { skus: recommendation.diagonalSkus.join(", ") })}
                            </span>
                        )}
                        {recommendation.skippedSkus.length > 0 && (
                            <span className="dispatch-recommend-banner-chip">
                                <AlertTriangle size={14} />
                                {t("box_recommendation_skipped", { count: String(recommendation.skippedSkus.length) })}
                            </span>
                        )}
                    </div>
                )}

                <div className="dispatch-box-tabs">
                    {boxes.map(box => {
                        const weightWarning = weightWarningFor(box);
                        const assigned = isBoxAssigned(box);
                        return (
                            <div
                                key={box.id}
                                className={`dispatch-box-tab ${activeBox === box.id ? "dispatch-box-tab--active" : ""} ${newBoxId === box.id ? "dispatch-box-tab--new" : ""} ${reassignTargetId === box.id ? "dispatch-box-tab--reassign" : ""} ${!assigned ? "dispatch-box-tab--unassigned" : ""} ${!assigned && promptShake ? "dispatch-box-tab--shake" : ""} ${weightWarning ? "dispatch-box-tab--overweight" : ""}`}
                                title={weightWarning ? weightWarningText(weightWarning) : undefined}
                            >
                                <button
                                    type="button"
                                    className="dispatch-box-tab-label"
                                    onClick={() => {
                                        // Click any tab's name → enter reassign
                                        // mode for THAT tab and make it active.
                                        // Clicking the already-targeted tab a
                                        // second time cancels reassign. The next
                                        // box scan resizes this tab; if the tab
                                        // holds items that also exits reassign
                                        // mode, so subsequent scans behave
                                        // normally (item → active box, box →
                                        // new tab). An empty tab stays targeted
                                        // — the operator is still picking a size.
                                        if (reassignTargetId === box.id) {
                                            setReassignTargetId(null);
                                            return;
                                        }
                                        setActiveBox(box.id);
                                        setReassignTargetId(box.id);
                                    }}
                                >
                                    {/* A tab with no box yet says what to do
                                        with it — SCAN BOX — in the place the
                                        box will land. */}
                                    {!assigned && (
                                        <span className="dispatch-box-tab-scan">{t("scan_box_prompt")}</span>
                                    )}
                                    {assigned && (
                                        <span className="dispatch-box-tab-name">
                                            {t("box_label")} {box.id}
                                        </span>
                                    )}
                                    <span className="dispatch-box-tab-size">
                                        {!assigned && (
                                            <><span className="dispatch-box-tab-name">{t("box_label")} {box.id}</span> · </>
                                        )}
                                        {box.boxName && <>{t("size_prefix")} {box.boxName} · </>}
                                        <span className="dispatch-box-tab-weight" data-testid={`box-weight-${box.id}`}>
                                            {(((box.boxWeightGrams ?? 0) + contentsWeightFor(box.id)) / 1000).toFixed(2)}kg
                                        </span>
                                    </span>
                                    {weightWarning && (
                                        <span className="dispatch-box-tab-warn" role="alert">
                                            <AlertTriangle size={12} /> {weightWarningText(weightWarning)}
                                        </span>
                                    )}
                                </button>
                                {/* Removable whenever there's another tab to fall
                                    back to, OR this lone box has an assignment to
                                    clear — removing the last box resets it to an
                                    unassigned Box 1 so the recommendation returns
                                    (reopen → unscan → repack). A single
                                    unassigned tab has nothing to remove. */}
                                {(boxes.length > 1 || assigned) && (
                                    <button
                                        type="button"
                                        className="dispatch-box-tab-remove"
                                        onClick={(e) => { e.stopPropagation(); removeBox(box.id); }}
                                        aria-label="Remove box"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {/* "+ › Own packaging" picked: the next item scan goes
                        into its own packaging. Not a real box until then. */}
                    {ownWait && (
                        <div className="dispatch-box-tab dispatch-box-tab--active dispatch-box-tab--own-wait" role="status">
                            <span className="dispatch-box-tab-label dispatch-box-tab-label--own-wait">
                                <PackageOpen size={22} aria-hidden="true" />
                                <span className="dispatch-box-tab-own-text">
                                    <span className="dispatch-box-tab-scan">{t("dispatch_own_pkg_scan_item")}</span>
                                    <span className="dispatch-box-tab-size">
                                        {t("box_label")} {(boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) : 0) + 1} · 0.00kg
                                    </span>
                                </span>
                            </span>
                            <button
                                type="button"
                                className="dispatch-box-tab-remove"
                                onClick={() => setOwnWait(false)}
                                aria-label={t("dispatch_own_pkg_cancel")}
                            >
                                <X size={12} />
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className={`dispatch-box-tab dispatch-box-tab--add${boxMenu ? " dispatch-box-tab--add-open" : ""}`}
                        aria-label={t("dispatch_new_container")}
                        aria-haspopup="menu"
                        aria-expanded={!!boxMenu}
                        onClick={e => toggleBoxMenu(e.currentTarget)}
                    >
                        <Plus size={22} />
                    </button>
                </div>

                {/* "+" menu: the next container. Whatever is picked, the
                    next scan goes into it. */}
                {boxMenu && (
                    <div
                        className="dispatch-box-menu"
                        role="menu"
                        aria-label={t("dispatch_new_container")}
                        style={{ left: boxMenu.left, bottom: boxMenu.bottom }}
                    >
                        <span className="dispatch-box-menu-title">{t("dispatch_new_container")}</span>
                        <button type="button" role="menuitem" className="dispatch-box-menu-item" onClick={openNewBoxTab}>
                            <span className="dispatch-box-menu-icon dispatch-box-menu-icon--box"><ScanBarcode size={22} aria-hidden="true" /></span>
                            <span className="dispatch-box-menu-text">
                                <span className="dispatch-box-menu-name">{t("dispatch_new_box")}</span>
                                <span className="dispatch-box-menu-hint">{t("dispatch_new_box_hint")}</span>
                            </span>
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="dispatch-box-menu-item"
                            onClick={() => {
                                setBoxMenu(null);
                                setOwnWait(true);
                            }}
                        >
                            <span className="dispatch-box-menu-icon dispatch-box-menu-icon--own"><PackageOpen size={22} aria-hidden="true" /></span>
                            <span className="dispatch-box-menu-text">
                                <span className="dispatch-box-menu-name">{t("dispatch_own_pkg_menu")}</span>
                                <span className="dispatch-box-menu-hint">{t("dispatch_own_pkg_menu_hint")}</span>
                            </span>
                        </button>
                        <span className="dispatch-box-menu-divider" aria-hidden="true" />
                        <button
                            type="button"
                            role="menuitem"
                            className="dispatch-box-menu-item dispatch-box-menu-item--minor"
                            onClick={() => {
                                setBoxMenu(null);
                                setOwnWait(false);
                                setCustomBoxForm({ length: "", width: "", height: "" });
                                setShowAddBoxModal(true);
                            }}
                        >
                            <span className="dispatch-box-menu-icon"><Ruler size={20} aria-hidden="true" /></span>
                            <span className="dispatch-box-menu-text">
                                <span className="dispatch-box-menu-name">{t("dispatch_other_size")}</span>
                                <span className="dispatch-box-menu-hint">{t("dispatch_other_size_hint")}</span>
                            </span>
                        </button>
                    </div>
                )}

                <DispatchSessionStats />

                {allResolved && order.items.some(i => shippedCount(i) > 0) && (
                    <button
                        className={`btn btn-success dispatch-complete-btn ${canComplete ? "dispatch-complete-btn--ready" : "dispatch-complete-btn--blocked"}`}
                        // Intentionally NOT `disabled` — we want the click
                        // to fire so we can toast the reason. Visual state
                        // is driven by the --blocked modifier instead.
                        title={
                            !hasScannedBox
                                ? t("scan_box_before_complete")
                                : !allBoxesHaveItems
                                    ? emptyBoxesMessage()
                                    : undefined
                        }
                        onClick={() => {
                            if (canComplete) {
                                handleCompleteClick();
                                return;
                            }
                            // Surface the specific reason as a toast — the
                            // disabled-look greyout alone wasn't telling
                            // operators why nothing was happening.
                            playError();
                            toast.dismiss();
                            if (flaggedPickBlocked) {
                                toast.error(t("fp_complete_blocked"), { toastId: "complete-blocked-flagged-pick" });
                                return;
                            }
                            if (!hasScannedBox) {
                                toast.error(t("scan_box_before_complete"), { toastId: "complete-blocked-no-box" });
                                // Nudge the SCAN BOX tab so the eye goes
                                // straight to what's blocking completion.
                                setPromptShake(true);
                                setTimeout(() => setPromptShake(false), 450);
                            } else if (!allBoxesHaveItems) {
                                toast.error(emptyBoxesMessage(), { toastId: "complete-blocked-empty-box" });
                            }
                        }}
                    >
                        {t("all_items_scanned")}
                    </button>
                )}

                {/* Resolve mode, nothing to ship (every unit refunded): no
                    box or booking is needed — Complete finalises the
                    refund-only resolution directly. */}
                {resolveMode && allResolved && !order.items.some(i => shippedCount(i) > 0) && (
                    <button
                        className="btn btn-success dispatch-complete-btn dispatch-complete-btn--ready"
                        onClick={() => resolveMode.onFinishNoShipment()}
                    >
                        {t("continue_label")}
                    </button>
                )}
            </div>

            {/* HiddenScanInput — global keydown listener for scanning */}
            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleScanSubmit}
                disabled={false}
                disableCamera
            />

            {/* Demo — toggled via Admin Settings → Demo Mode. Two stacked
                rows inside one panel: Boxes on top (real barcodes for the
                operator's warehouse) and Items below (real SKUs from the
                order being packed). Tapping any button feeds the same
                handleScanSubmit pipeline a hardware scan would. */}
            {DEMO_MODE && (demoBoxes.length > 0 || order.items.some(i => !isItemResolved(i))) && (
                <div className="dispatch-demo-panel dispatch-demo-panel--stacked" style={{ bottom: 110 }}>
                    {demoBoxes.length > 0 && (
                        <div className="dispatch-demo-row">
                            <span className="dispatch-demo-label">Boxes</span>
                            {demoBoxes.map(b => (
                                <button
                                    key={b.barcode}
                                    className="dispatch-demo-btn"
                                    onClick={() => handleScanSubmit(b.barcode)}
                                    title={b.name}
                                >
                                    {b.barcode}
                                </button>
                            ))}
                        </div>
                    )}
                    {order.items.some(i => !isItemResolved(i)) && (
                        <div className="dispatch-demo-row">
                            <span className="dispatch-demo-label">Items</span>
                            {order.items.filter(i => !isItemResolved(i)).map(item => (
                                <button
                                    key={item.item_id}
                                    className="dispatch-demo-btn"
                                    onClick={() => handleScanSubmit(item.sku)}
                                >
                                    {item.sku}{item.quantity > 1 ? ` ${resolvedCount(item)}/${item.quantity}` : ""}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Override-reason modal — the operator scanned a different box
                to the recommended one. Picking a reason KEEPS the scanned
                box (and logs the override); closing — the X or clicking
                outside — treats it as a mis-scan: the box choice is rolled
                back and nothing is logged. The wrong box can only stay on
                the order with a reason attached. Scans are swallowed while
                the modal is open. */}
            {overridePrompt && (() => {
                const closeWith = (reason: string) => {
                    postRecommendationLog({
                        recommendedBarcode: overridePrompt.recommendedBarcode,
                        recommendedName: overridePrompt.recommendedName,
                        scannedBarcode: overridePrompt.scannedBarcode,
                        scannedName: overridePrompt.scannedName,
                        accepted: false,
                        reason,
                        diagonal: overridePrompt.diagonal,
                    });
                    setOverridePrompt(null);
                };
                // Mis-scan: undo the box choice this modal was asking about.
                // prevBox restores the tab's pre-scan state (keeping any
                // item assignments it already had); prevBox null removes
                // the tab that the scan created — falling back to a fresh
                // unassigned Box 1 if it was the only tab.
                const dismiss = () => {
                    const revert = overridePrompt.revert;
                    if (revert && boxes.some(b => b.id === revert.boxId)) {
                        if (revert.prevBox) {
                            const prev = revert.prevBox;
                            onUpdateBoxes(boxes.map(b => b.id === revert.boxId ? { ...prev, items: b.items } : b));
                        } else if (order.items.some(i => i.assignedBoxes.includes(revert.boxId))) {
                            // Shouldn't happen (scans are swallowed while the
                            // modal is open), but never strand assignments —
                            // just clear the tab's box choice.
                            onUpdateBoxes(boxes.map(b => b.id === revert.boxId ? { id: b.id, items: b.items } : b));
                        } else {
                            const remaining = boxes.filter(b => b.id !== revert.boxId);
                            onUpdateBoxes(remaining.length > 0 ? remaining : [{ id: 1, items: [] }]);
                        }
                        hasAutoBooked.current = false;
                        preloadResultRef.current = null;
                        preloadingRef.current = false;
                    }
                    setOverridePrompt(null);
                };
                return (
                    <div className="modal-overlay" onClick={dismiss}>
                        <div className="modal-card" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>{t("box_override_title")}</h2>
                                <button className="modal-close" onClick={dismiss} aria-label={t("cancel")}>
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="modal-card-body">
                                <p className="dispatch-override-question">
                                    {t("box_override_question", {
                                        scanned: overridePrompt.scannedName,
                                        recommended: overridePrompt.recommendedName,
                                    })}
                                </p>
                                <div className="dispatch-override-grid">
                                    {OVERRIDE_REASONS.map(({ code, icon: Icon }) => (
                                        <button
                                            key={code}
                                            type="button"
                                            className="dispatch-override-option"
                                            onClick={() => closeWith(code)}
                                        >
                                            <Icon size={26} />
                                            <span>{t(`box_override_reason_${code}`)}</span>
                                        </button>
                                    ))}
                                </div>
                                <p className="admin-settings-section-hint" style={{ marginTop: 12 }}>
                                    {t("box_override_dismiss_hint")}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Confirm leaving an order with progress — exiting discards
                the desk-side scans/boxes (the order stays 'dispatch'
                BE-side and reopens fresh from the tote). */}
            {confirmLeave && (
                <div className="modal-overlay" onClick={() => setConfirmLeave(false)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("leave_order")}</h2>
                            <button className="modal-close" onClick={() => setConfirmLeave(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p>{t("leave_order_warning", { order: order.order_number })}</p>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost btn-lg" onClick={() => setConfirmLeave(false)}>
                                    {t("cancel")}
                                </button>
                                <button className="btn btn-danger btn-lg" onClick={() => { setConfirmLeave(false); goBack(); }}>
                                    {t("leave_order")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset order — the whole desk back to a fresh open. */}
            {confirmReset && (
                <div className="modal-overlay" onClick={() => setConfirmReset(false)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("reset_order")}</h2>
                            <button className="modal-close" onClick={() => setConfirmReset(false)} aria-label={t("cancel")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p>
                                {boxes.length === 1
                                    ? t("reset_order_confirm_one")
                                    : t("reset_order_confirm", { count: String(boxes.length) })}
                            </p>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setConfirmReset(false)}>
                                    {t("cancel")}
                                </button>
                                <button className="btn btn-danger" onClick={doResetOrder}>
                                    <RotateCcw size={18} />
                                    {t("reset_order")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm remove box modal */}
            {confirmRemoveBoxId !== null && (() => {
                const boxItems = order.items.filter(i => i.assignedBoxes.includes(confirmRemoveBoxId));
                const boxQty = boxItems.reduce((sum, i) => i.assignedBoxes.filter(b => b === confirmRemoveBoxId).length, 0);
                return (
                    <div className="modal-overlay" onClick={() => setConfirmRemoveBoxId(null)}>
                        <div className="modal-card" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>{t("remove_box")}</h2>
                                <button className="modal-close" onClick={() => setConfirmRemoveBoxId(null)}><X size={18} /></button>
                            </div>
                            <div className="modal-card-body">
                                <div className="dispatch-remove-box-icon">
                                    <Trash2 size={36} />
                                </div>
                                <p className="dispatch-remove-box-title">
                                    {t("box_label")} {confirmRemoveBoxId}
                                </p>
                                <p className="dispatch-remove-box-question">
                                    {t("remove_box_confirm_question")}
                                </p>
                                <div className="dispatch-remove-box-items">
                                    <Package size={16} />
                                    <span>{boxQty} {boxQty === 1 ? t("item_singular") : t("item_plural")} {t("will_be_unscanned")}</span>
                                </div>
                                <div className="dispatch-remove-box-list">
                                    {boxItems.map(item => (
                                        <div key={item.item_id} className="dispatch-remove-box-item">
                                            <span className="dispatch-remove-box-sku">{item.sku}</span>
                                            {item.scannedCount > 1 && <span className="dispatch-remove-box-qty">x{item.scannedCount}</span>}
                                        </div>
                                    ))}
                                </div>
                                <div className="modal-confirm-buttons" style={{ marginTop: 20 }}>
                                    <button className="btn btn-ghost btn-lg" onClick={() => setConfirmRemoveBoxId(null)}>
                                        {t("no")}
                                    </button>
                                    <button className="btn btn-danger btn-lg" onClick={() => doRemoveBox(confirmRemoveBoxId)}>
                                        <Trash2 size={18} />
                                        {t("yes")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Add-box modal — shown when the operator taps the `+` tab.
                Scanning a real box anywhere in the page (the global keydown
                listener stays live) closes this modal automatically; the
                form path lets them enter ad-hoc dimensions for a pallet
                that isn't in the dispatch_boxes table. */}
            {showAddBoxModal && (
                <div className="modal-overlay" onClick={() => setShowAddBoxModal(false)}>
                    <div className="modal-card dispatch-add-box-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("add_box")}</h2>
                            <button className="modal-close" onClick={() => setShowAddBoxModal(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="dispatch-add-box-hint">{t("add_box_hint")}</p>
                            <div className="dispatch-add-box-grid">
                                <label className="dispatch-add-box-label">
                                    {t("length_cm")}
                                    {/* Deliberately NOT autofocused — while this modal
                                        invites a real box scan, a focused input would
                                        swallow the scanner keystrokes. */}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-add-box-input"
                                        value={customBoxForm.length}
                                        onChange={e => setCustomBoxForm(f => ({ ...f, length: e.target.value }))}
                                    />
                                </label>
                                <label className="dispatch-add-box-label">
                                    {t("width_cm")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-add-box-input"
                                        value={customBoxForm.width}
                                        onChange={e => setCustomBoxForm(f => ({ ...f, width: e.target.value }))}
                                    />
                                </label>
                                <label className="dispatch-add-box-label">
                                    {t("height_cm")}
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="dispatch-add-box-input"
                                        value={customBoxForm.height}
                                        onChange={e => setCustomBoxForm(f => ({ ...f, height: e.target.value }))}
                                    />
                                </label>
                            </div>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddBoxModal(false)}>
                                    {t("cancel")}
                                </button>
                                <button className="btn btn-success btn-sm" onClick={applyCustomBox}>
                                    {t("add_box")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* "Ship in own packaging" — measure the container and say how
                many units go in it. Same field layout as the add-box modal;
                the extra Units field is what lets several of the same SKU
                strapped together book as ONE parcel. */}
            {ownPkgPrompt && (() => {
                const item = order.items.find(i => i.item_id === ownPkgPrompt.itemId);
                if (!item) return null;
                const maxUnits = ownPackagingUnits(item);
                // What Confirm will do, recalculated as the Units field
                // changes: which box each unit comes out of.
                const plan = planOwnPackaging(item, boxes, {
                    units: Number(ownPkgForm.qty) || 1,
                    unscanned: item.quantity - resolvedCount(item),
                    prefer: ownPkgPrompt.prefer,
                });
                const boxedSources = plan.fromBoxes.filter(s => s.boxed);
                // Where the prefilled numbers came from, so the packer knows
                // whether to trust them or check them.
                const { defaults } = ownPkgPrompt;
                const savedAt = defaults.spec?.updated_at ? new Date(defaults.spec.updated_at) : null;
                const savedWhen = savedAt && !Number.isNaN(savedAt.getTime()) ? savedAt.toLocaleDateString() : "";
                const savedBy = defaults.spec?.updated_by_name || "—";
                const prefillHint = defaults.source === "sku"
                    ? t("ship_in_packaging_prefilled_sku", { name: savedBy, date: savedWhen })
                    : defaults.source === "asin"
                    ? t("ship_in_packaging_prefilled_asin", {
                        sku: defaults.spec?.source_sku ?? "",
                        asin: defaults.spec?.asin ?? "",
                        name: savedBy,
                        date: savedWhen,
                    })
                    : defaults.source === "catalogue"
                    ? t("ship_in_packaging_prefilled_catalogue")
                    : null;
                // Units changed and the packer has not typed a weight: keep
                // the weight following the unit count.
                const setQty = (qty: string) => setOwnPkgForm(f => {
                    const n = Math.floor(Number(qty));
                    const follows = !f.weightEdited && defaults.unitWeightG != null && n > 0;
                    return { ...f, qty, ...(follows ? { weight: kgField((defaults.unitWeightG as number) * n) } : {}) };
                });
                return (
                    <div className="modal-overlay" onClick={() => setOwnPkgPrompt(null)}>
                        <div className="modal-card dispatch-add-box-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-card-header">
                                <h2>{t("ship_in_own_packaging_action")}</h2>
                                <button className="modal-close" onClick={() => setOwnPkgPrompt(null)}>
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="modal-card-body">
                                <p className="dispatch-add-box-hint">{item.title}</p>
                                {prefillHint && (
                                    <p className="dispatch-add-box-hint dispatch-own-pkg-prefill" role="note">{prefillHint}</p>
                                )}
                                <div className="dispatch-add-box-grid">
                                    <label className="dispatch-add-box-label">
                                        {t("length_cm")}
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            className="dispatch-add-box-input"
                                            value={ownPkgForm.length}
                                            onChange={e => setOwnPkgForm(f => ({ ...f, length: e.target.value }))}
                                        />
                                    </label>
                                    <label className="dispatch-add-box-label">
                                        {t("width_cm")}
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            className="dispatch-add-box-input"
                                            value={ownPkgForm.width}
                                            onChange={e => setOwnPkgForm(f => ({ ...f, width: e.target.value }))}
                                        />
                                    </label>
                                    <label className="dispatch-add-box-label">
                                        {t("height_cm")}
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            className="dispatch-add-box-input"
                                            value={ownPkgForm.height}
                                            onChange={e => setOwnPkgForm(f => ({ ...f, height: e.target.value }))}
                                        />
                                    </label>
                                    {/* The container as weighed. Blank = use the
                                        catalogue weight, as before. */}
                                    <label className="dispatch-add-box-label">
                                        {t("ship_in_packaging_weight_kg")}
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            step="0.01"
                                            min={0}
                                            className="dispatch-add-box-input"
                                            value={ownPkgForm.weight}
                                            onChange={e => setOwnPkgForm(f => ({ ...f, weight: e.target.value, weightEdited: true }))}
                                        />
                                    </label>
                                    {maxUnits > 1 && (
                                        <label className="dispatch-add-box-label">
                                            {t("units_in_container")}
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                min={1}
                                                max={maxUnits}
                                                className="dispatch-add-box-input"
                                                value={ownPkgForm.qty}
                                                onChange={e => setQty(e.target.value)}
                                            />
                                        </label>
                                    )}
                                </div>
                                {/* The line is already in a box: say which one to
                                    empty, and what happens to it. */}
                                {boxedSources.length > 0 && (
                                    <div className="dispatch-own-pkg-notes">
                                        {boxedSources.map(source => {
                                            const box = boxes.find(b => b.id === source.boxId);
                                            const label = box?.boxName ? `${source.boxId} (${box.boxName})` : String(source.boxId);
                                            return (
                                                <div key={source.boxId} className="dispatch-own-pkg-note" role="status">
                                                    <Package size={22} aria-hidden="true" />
                                                    <span className="dispatch-own-pkg-note-text">
                                                        <strong>{t("dispatch_own_pkg_take_out", { count: String(source.units), box: label })}</strong>
                                                        <span>
                                                            {source.left === 0
                                                                ? t("dispatch_own_pkg_box_emptied", { id: String(source.boxId) })
                                                                : t("dispatch_own_pkg_box_left", { id: String(source.boxId), count: String(source.left) })}
                                                        </span>
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {plan.fresh > 0 && (
                                            <div className="dispatch-own-pkg-note dispatch-own-pkg-note--plain">
                                                <ScanBarcode size={22} aria-hidden="true" />
                                                <span className="dispatch-own-pkg-note-text">
                                                    {t("dispatch_own_pkg_unscanned", { count: String(plan.fresh) })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                    <button className="btn btn-ghost btn-sm" onClick={() => setOwnPkgPrompt(null)}>
                                        {t("cancel")}
                                    </button>
                                    <button
                                        className="btn btn-success btn-sm"
                                        onClick={() => {
                                            const lCm = Number(ownPkgForm.length);
                                            const wCm = Number(ownPkgForm.width);
                                            const hCm = Number(ownPkgForm.height);
                                            const weightText = ownPkgForm.weight.trim().replace(",", ".");
                                            const weightKg = weightText === "" ? null : Number(weightText);
                                            if (!(lCm > 0) || !(wCm > 0) || !(hCm > 0)
                                                || (weightKg !== null && !(weightKg > 0))) {
                                                playError();
                                                toast.dismiss();
                                                toast.error(t("box_form_invalid"));
                                                return;
                                            }
                                            applyOwnPackaging(ownPkgPrompt.itemId, {
                                                lCm, wCm, hCm,
                                                weightG: weightKg === null ? null : Math.round(weightKg * 1000),
                                                units: Number(ownPkgForm.qty) || 1,
                                                prefer: ownPkgPrompt.prefer,
                                            });
                                            setOwnPkgPrompt(null);
                                        }}
                                    >
                                        {t("confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {carrierPanel && (
                <DispatchBookingStatus
                    panel={carrierPanel}
                    onRetry={retryCarrier}
                    retrying={carrierRetrying}
                    onRebook={rebookCarrier}
                    rebooking={carrierRebooking}
                    onClose={dismissCarrierPanel}
                    onFlag={onFlagOrder}
                    addressEditor={{
                        address: order.delivery_address,
                        onSave: saveDeliveryAddress,
                    }}
                />
            )}

        </div>
    );
};

export default DispatchOrderPack;
